'use client';
/**
 * Module RH — Planning & Postes (lot 1).
 *
 * Le parti pris de ces deux écrans : le blocage par habilitation ne doit pas
 * être une surprise au moment d'enregistrer. On montre AVANT de cliquer qui
 * peut tenir le poste et qui ne le peut pas, et pourquoi. Un refus qui arrive
 * après coup se vit comme une panne ; annoncé, il se lit comme une règle.
 *
 * ⚠️ Ce fichier n'applique AUCUN droit : il masque et il explique. Le contrôle
 * réel est côté serveur (`backend/src/api/ged/controllers/rh.ts`), y compris le
 * refus des non-habilités et la réserve de la dérogation au RQ.
 */
import React from 'react';
import { enTeteCollant, Tableau } from '@/shared/Tableau';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, joursSemaine, lundiDe } from '@/lib/store';
import { peutLirePlanning, peutVoirPrevisionnel } from '@/lib/acces-planning';
import { grouperLignes, CATEGORIES_POSTE } from '@/lib/blocs-planning';
import { EtatHabilitationsParc } from '@/components/screens/PostesEquipements';
import type { RhAbsence, RhAffectation, RhPersonnel, RhPoste, RhOuverture, RhCalendrier, RhRegleOuverture } from '@/lib/types';
import { useRemonteEnHaut, VOILE, I, Eyebrow, TitrePage, card, inputStyle, EmptyState, Help, RefISO, ConfirmButton, useEscape, FilterBar, Skeleton, Selecteur, MenuActions, useConfirmation, PoigneeColonne, Modale } from '@/components/ui';
import { PlanningMois, ChoixMois, decalerMois } from '@/components/screens/RhPlanningMois';
import ReglesAffectation from '@/components/screens/ReglesAffectation';
/* `teinteDuPoste` / `couleursPrisesPostes` : la couleur de repère d'un POSTE
   (RQ, 05/10/2026) — même palette, même sélecteur et même module que la fiche
   du personnel ; règle d'unicité GLOBALE (voir l'en-tête du module). */
import { pastilleDe, couleursPrisesPostes, teinteDuPoste } from '@/lib/couleurs-personne';
import { SelecteurCouleur } from '@/components/SelecteurCouleur';
import { abregePoste } from '@/lib/abrege-poste';
import { PlanningDndContext, AffectationGlissable, CaseDeposable, type AffectationGlissee, type CibleCase } from '@/components/PlanningDnd';
import { verdictLocal } from '@/lib/echange-planning';
import { lireLargeurs, ecrireLargeurs, oublierLargeurs, borner, LARGEUR_MIN, type Largeurs } from '@/lib/largeurs-colonnes';
import { useSortieFenetre, AlerteSortie } from '@/shared/fenetreModale';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';
import { nomDe, prenomDe } from '@/lib/nom';
import { PrevisionnelMois } from '@/components/screens/PrevisionnelMois';
import { BandeauConventions, LignesConventions } from '@/components/BandeauConventions';
import { useOccurrences, parDate as conventionsParDate } from '@/lib/conventions';
import { FAMILLES_PERSONNEL, familleDe, sansTitre } from '@/lib/familles-personnel';
import { EchangesPlanning } from '@/components/screens/EchangesPlanning';
/* Le calendrier des conventions, DÉPLACÉ ici en onglet le 05/10/2026 (il
   gardait une adresse à lui, hors de la barre latérale). Export par défaut,
   contrairement à ses voisins. */
import Calendrier from '@/components/screens/Calendrier';
import { PublicationPlanning } from '@/components/screens/PublicationPlanning';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
import { isoJour } from '@/lib/meta';
import { libelleMois, libelleJourLong } from '@/lib/jour';
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/**
 * Le prénom, tiré du nom complet.
 *
 * ⚠️ Le premier mot n'est pas toujours le prénom : « Dr Inès Harzallah »
 * commence par un titre. On les saute — sinon la case afficherait « Dr » pour
 * les quatre biologistes, c'est-à-dire rien qui les distingue.
 */
/* ⚠️ `prenomDe` NE VIT PLUS ICI (07/09/2026). Ce fichier en portait un
   doublon qui découpait une CHAÎNE — le premier mot du nom composé, en sautant
   le titre. Depuis la séparation des colonnes du 06/09, la chaîne reçue est le
   PATRONYME SEUL : la case du planning affichait « Harzallah » là où elle
   promettait « Inès ». Celui du socle (`lib/nom.ts`) lit la colonne `prenom`
   et ne retombe sur le découpage que si elle est vide. Une seule version. */
/* ⚠️ LA LISTE DES CATÉGORIES EST PARTAGÉE (30/08/2026). Celle qui vivait ici
   offrait `technique · preanalytique · garde · support` — ni `reception`, ni
   `qualite`, que douze postes portaient pourtant. Rouvrir l'un d'eux dans le
   formulaire et enregistrer l'aurait basculé en `technique` sans un mot, et le
   planning l'aurait rangé dans le mauvais bloc. Une seule liste, dans
   `blocs-planning`, parce que c'est elle qui décide aussi des blocs. */
const CATEGORIES: Record<string, string> = Object.fromEntries(
  CATEGORIES_POSTE.map((c) => [c.cle, c.libelle]),
);
const MODES_GARDE: Record<string, string> = {
  aucun: 'Pas une garde', presence: 'Présence sur place', astreinte: 'Astreinte à domicile',
};

const fmtJour = (iso: string) => {
  const [, m, j] = iso.split('-');
  return `${j}/${m}`;
};

/** `AAAA-MM-JJ` en LOCAL — jamais `toISOString()`, qui bascule d'un jour en
 *  fin de soirée à Tunis (même raison que `auj`, plus bas dans l'écran). */
const fmtISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Absence couvrant `date` pour cette personne.
 *
 * Seuls `validee` et `demandee` comptent : une demande refusée ou annulée ne dit
 * rien du planning. La demande non encore tranchée est signalée à part — ce
 * n'est pas encore un fait, mais planifier dessus prépare un conflit.
 *
 * ⚠️ On ne lit ici QUE les dates et le statut. Le motif d'absence est une donnée
 * de santé : le planning n'en a pas besoin pour savoir qu'il ne faut pas
 * affecter quelqu'un, et le RQ n'a pas à l'apprendre en passant.
 */
function absenceLe(absences: RhAbsence[], orgId: string, date: string): RhAbsence | undefined {
  return absences.find((a) =>
    a.personnel?.orgId === orgId
    && (a.statut === 'validee' || a.statut === 'demandee')
    && a.dateDebut <= date && date <= a.dateFin);
}

/**
 * Verdict local, calculé pour l'AFFICHAGE seulement (le serveur refait tout).
 * ⚠️ LA FONCTION `verdictLocal` VIT DÉSORMAIS dans `@/lib/echange-planning`
 * (26/10/2026) : l'échange du mois, en glissé, doit utiliser la MÊME règle que
 * le dialogue d'affectation. Cette note en garde l'histoire.
 *
 * ── ⚠️ LE TRI ÉTAIT MORT, PAS ABSENT (corrigé le 27/08/2026) ────────────────
 *
 * La fenêtre séparait bien « Habilités » et « Non habilités », mais la ligne
 * qui décidait était celle-ci :
 *
 *     if (typeof e !== 'string') { indetermine.push(e.code); continue; }
 *
 * Une exigence en forme d'OBJET — `{ code, niveauMin }` — était mise de côté
 * dans `indetermine`, qui n'entrait pas dans `ok`. Or depuis la bascule du
 * modèle, les vingt-cinq postes ne portent PLUS QUE des exigences objet : il
 * n'en reste aucune en forme de chaîne. Le verdict renvoyait donc `ok: true`
 * pour tout le monde, sur tous les postes. « Habilités » contenait l'effectif
 * entier et « Non habilités » était toujours vide.
 *
 * Le calcul juste existait déjà, écrit correctement — dans un AUTRE écran
 * (`ressources/Habilitations.tsx`, fonction `deduit`). Il est ici.
 *
 * ── Ce qu'il lit désormais ─────────────────────────────────────────────────
 *
 * `rhDetenues` — les habilitations réellement détenues (`ged-habilitation-
 * personne`), et non plus `ged-setting.habilitations`. Cette matrice-là est en
 * cours de retrait : ses axes sont des codes de poste dont six sont périmés
 * depuis le renommage des paillasses, et elle ne bloquait plus rien.
 *
 * ⚠️ La forme CHAÎNE reste lue, parce que rien ne garantit qu'aucun poste n'en
 * porte plus jamais : une exigence qu'on ne sait pas évaluer est déclarée
 * `indetermine` et laissée au serveur, jamais comptée comme satisfaite.
 */
/** Clef de recherche : sans accent, sans casse — « Nour Eddine » trouve « NOUREDDINE ». */
const clefRecherche = (t: string) =>
  (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();



/** Une exigence se lit soit comme une discipline, soit comme une habilitation. */
function libelleExigence(
  e: string | { code: string; niveauMin?: string | null },
  refs: { code: string; libelle: string }[],
): string {
  if (typeof e === 'string') return e;
  const r = refs.find((x) => x.code === e.code);
  return (r?.libelle || e.code) + (e.niveauMin ? ` (${e.niveauMin})` : '');
}

/* ============================ PLANNING ============================ */

export function Planning() {
  const { rhAcces, rhPostes, rhPersonnel, rhAffectations, rhOuvertures, rhAbsences, rhSemaine, setRhSemaine, session, settings, affecter, retirerAffectation, derogerAbsence, loadRhGestion, rhChargement, rhHabilitations, rhDetenues, rhPlanningModifiable, showToast, naviguerVers, loadRh, loadMaintenances, rhMois, setRhMois } = useGed();
  const [cible, setCible] = React.useState<{ date: string; poste: RhPoste } | null>(null);
  const [cibleDerogation, setCibleDerogation] = React.useState<RhAffectation | null>(null);
  // « Par poste » sert à AFFECTER (une case = un poste à couvrir) ; « par
  // personne » sert à LIRE la charge de chacun sur la semaine. Deux questions
  // distinctes, que la même grille ne peut pas répondre en même temps.
  const [vue, setVue] = React.useState<'poste' | 'personne'>('poste');
  /* A3 — la PÉRIODE est un axe distinct de la disposition : « par poste » et
     « par personne » gardent leur sens sur les deux échelles. La semaine
     écrit, le mois relit. */
  /* `jour` (RQ, 04/10/2026) : la grille de la semaine réduite à UN jour — mêmes
     cases, mêmes gestes, la place de lire tout le monde sur chaque poste. */
  /* Le planning s'ouvre sur le MOIS (RQ, 26/10/2026) : la vue d'ensemble d'abord,
     la semaine pour écrire. */
  const [periode, setPeriode] = React.useState<'jour' | 'semaine' | 'mois'>('mois');
  const [jourVu, setJourVu] = React.useState('');
  /* ── LA BARRE DE COMMANDES EST UNE, POUR LES TROIS ÉCHELLES (RQ, 26/10/2026) ──
   * « Je ne m'y retrouve pas » : la navigation, l'impression et les réglages du
   * MOIS vivaient dans une seconde barre, sous celle de la semaine, avec leurs
   * propres libellés (« Mois précédent » / « Ce mois-ci » contre des flèches et
   * « Aujourd'hui »). Trois endroits pour une même idée. Désormais :
   *   · la ligne du haut dit OÙ l'on est (période, flèches, titre, aujourd'hui)
   *     et ce qu'on peut FAIRE (à droite) — dans les trois échelles ;
   *   · la ligne du bas dit COMMENT on le voit (vue, sens, densité, personnes) ;
   *   · un réglage qui ne s'applique pas à l'échelle ouverte est ABSENT, pas
   *     grisé : un bouton inerte se lit comme une panne.
   * Le mois garde son code (état, impression, PDF) et vient se poser dans ces
   * deux emplacements — voir `emplacements` de `PlanningMois`. */
  const [emplActions, setEmplActions] = React.useState<HTMLElement | null>(null);
  const [emplAffichage, setEmplAffichage] = React.useState<HTMLElement | null>(null);
  /* ⚠️ LE SENS DE LECTURE EST PARTAGÉ PAR LES DEUX VUES (RQ, 30/08/2026).
     Il vivait dans la vue Mois seule, et la RQ ne l'y trouvait pas : « je ne
     trouve pas le bouton pour transposer ». Un réglage rangé dans l'onglet
     qu'il sert n'est visible que de ceux qui y sont déjà. Remonté à côté du
     sélecteur Semaine/Mois, il se voit quel que soit l'onglet actif — et il
     vaut pour les deux, parce que c'est la même question posée deux fois. */
  const [sens, setSens] = React.useState<'jours-en-colonnes' | 'postes-en-colonnes'>('jours-en-colonnes');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(vue);
  /** Poste dont la DÉFINITION est ouverte en fenêtre (RQ, 19/08/2026). */
  const [posteOuvert, setPosteOuvert] = React.useState<RhPoste | null>(null);
  const [focus, setFocus] = React.useState<'derogations' | 'ecarts' | null>(null);
  /* A20 — LE PLANNING PAR FONCTION (RQ, 25/08/2026 : « je voudrais pouvoir
     afficher un planning par fonction — technique ou bien biologiste »).
     Le filtre porte sur les fonctions DÉCLARÉES du poste (`p.fonctions`), pas
     sur sa catégorie : deux postes de même catégorie peuvent relever de
     fonctions différentes, et c'est justement ce qu'on veut distinguer. */
  const [fonctionVue, setFonctionVue] = React.useState('');
  /* PASSAGES PAR POSTE sur l'année affichée (RQ, 19/08/2026) — la matière de
     la réhabilitation annuelle. Le SEUIL est un réglage de lecture (il aide à
     repérer), pas une décision : prononcer la réhabilitation reste un acte
     humain, signé ailleurs. Il se garde par navigateur. */
  /* ── DENSITÉ (Lot 1, 02/09/2026) ─────────────────────────────────────────
   * Mesuré le 01/09 : 74 px par ligne pour une seule ligne de texte, dont 20 px
   * de `padding-top` qui n'existaient que pour loger le bouton « + ». Trente-deux
   * lignes à l'écran, et le reste sous le pli.
   *
   * ⚠️ PAR UTILISATEUR, ET NON PAR LABORATOIRE. Ce n'est pas une règle qualité :
   * c'est ce que l'œil de chacun préfère, et ça dépend de son écran. Le ranger
   * dans le paramétrage en ferait une décision de la RQ pour tout le monde.
   *
   * ⚠️ PLANCHER À 28 px EN COMPACT. La cible tactile utile doit rester ≥ 24 px
   * (WCAG 2.2 AA, « Target Size — Minimum ») : le planning se consulte sur
   * tablette, et une ligne plus basse rendrait la corbeille et le « + »
   * impossibles à viser au doigt. On gagne de la place, on ne la vole pas au
   * geste. */
  /* ⚠️ LA DENSITÉ « CONFORTABLE » A ÉTÉ RETIRÉE (RQ, 26/10/2026 : « le compact me
     semble largement suffisant »). Un second réglage obligeait à se demander
     lequel choisir, doublait les chemins de rendu à tenir, et n'apportait que
     des lignes plus aérées. Le planning est compact, point — plancher de 28 px
     conservé pour la cible tactile. Les HEURES de chaque case, qu'il masque,
     sont dans son infobulle (`Case`). */
  /* La couleur choisie d'une personne, indexée sur TOUT l'effectif — sortis
     compris : une semaine passée les affiche encore, et un départ ne doit pas
     changer l'apparence d'un planning archivé. */
  const couleurDe = React.useMemo(() => {
    const par = new Map(rhPersonnel.map((p) => [String(p.orgId), p.couleur || null]));
    return (cle: string) => par.get(cle) || null;
  }, [rhPersonnel]);

  const [voirPassages, setVoirPassages] = React.useState(false);
  const [seuil, setSeuil] = React.useState<number>(() => {
    try { return Number(localStorage.getItem('bio24.seuil-rehabilitation')) || 10; } catch { return 10; }
  });

  // `loadRhGestion` ne charge qu'UNE année, au démarrage. Sans ce rattrapage, un
  // planning affiché sur janvier prochain n'aurait aucune absence en mémoire et
  // se tairait — un écran qui se tait passe pour un écran qui n'a rien à dire.
  // Le ref part sur l'année courante : celle du démarrage n'est pas rechargée.
  const anneeSemaine = rhSemaine.slice(0, 4);
  const anneeChargee = React.useRef(String(new Date().getFullYear()));
  React.useEffect(() => {
    if (!rhAcces?.acces || anneeChargee.current === anneeSemaine) return;
    anneeChargee.current = anneeSemaine;
    void loadRhGestion(Number(anneeSemaine));
  }, [anneeSemaine, rhAcces?.acces, loadRhGestion]);

  // Lot N6 — le planning se LIT largement et ne s'ÉCRIT qu'à la RH. La lecture
  // n'est donc plus conditionnée au rôle `rh` ; ce qui l'est, c'est l'écriture,
  // et `rhPlanningModifiable` vient du serveur avec les affectations. Le refus
  // d'écran ne tombe que si le moteur a refusé la lecture elle-même.
  const peutLire = peutLirePlanning(rhAcces, session.sections);
  /* ⚠️ LE REFUS D'ÉCRAN EST RENDU EN FIN DE COMPOSANT, PAS ICI (02/09/2026).
   *
   * Il coupait la fonction AVANT huit crochets — deux `useMemo`, quatre
   * `useState`, deux `useMemo` encore. React identifie un état par son RANG
   * d'appel : un composant qui appelle huit crochets de plus au rendu suivant
   * n'a plus les mêmes états aux mêmes rangs. `peutLire` dépend de la session
   * et des sections, qui arrivent APRÈS le premier rendu — la bascule se
   * produit donc à chaque ouverture de l'écran, pas dans un cas limite.
   *
   * Symptômes possibles : « Rendered more hooks than during the previous
   * render », ou pire, des états qui prennent la valeur d'un autre — le seuil
   * de réhabilitation qui devient le jour à propager. Rien à l'écran ne dit
   * lequel des deux se produit.
   *
   * La règle : aucun retour anticipé avant le dernier crochet. Le refus se
   * décide ici, il se REND à la fin. */
  const modifiable = rhPlanningModifiable;
  /* ── ÉCRIRE EST UN MODE, POUR LES TROIS ÉCHELLES (RQ, 26/10/2026) ──────────
   * « Trop facilement modifiable » : en semaine, chaque case portait en
   * permanence son « + » et son « − », et le glissé était armé — un clic de
   * travers et le planning changeait. Le mois, lui, ne s'écrivait qu'après un
   * bouton « Modifier » : deux règles pour un même planning.
   * Désormais `modifiable` dit seulement « ce profil PEUT écrire » ; écrire
   * demande en plus d'ouvrir le mode « Modifier », fermé par défaut, le même
   * bouton à la même place en Jour, Semaine et Mois. */
  const [modeEdition, setModeEdition] = React.useState(false);
  const ecriture = modifiable && modeEdition;

  /* Mémoïsé : `postesVus` en dépend, et un tableau reconstruit à chaque rendu
     ferait recalculer toute la chaîne pour rien. */
  const jours = React.useMemo(() => joursSemaine(rhSemaine), [rhSemaine]);
  /* Les conventions de la semaine, lues UNE fois : la ligne au-dessus de la grille
     et les petits points sous chaque date (RQ, 04/10/2026 — « discrètement »). */
  const { occurrences: occConventions } = useOccurrences(jours[0] || '', jours[jours.length - 1] || '');
  const conventionsDuJour = React.useMemo(() => conventionsParDate(occConventions), [occConventions]);
  /* Le jour même, pour le repérer dans la grille. Calculé en LOCAL et non en
     UTC : `toISOString()` bascule d'un jour en fin de soirée à Tunis, et la
     colonne teintée aurait sauté au lendemain à partir de 23 h. */
  const auj = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })();
  /* Le jour affiché en vue Jour : celui choisi s'il est dans la semaine chargée,
     sinon aujourd'hui s'il y est, sinon le lundi. */
  const jourCourant = jours.includes(jourVu) ? jourVu : jours.includes(auj) ? auj : jours[0]!;
  const joursAffiches = periode === 'jour' ? [jourCourant] : jours;
  const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const decalerJour = (n: number) => {
    const [a, m, j] = jourCourant.split('-').map(Number);
    const d = new Date(a!, m! - 1, j! + n);
    setJourVu(isoLocal(d));
    setRhSemaine(lundiDe(d));
  };
  /* Mémoïsé pour que `postesVus` puis `blocsPostes` aient une identité stable
     (02/09/2026). Sans cela, `blocsPostes` dépendait d'une CHAÎNE fabriquée
     (`postesVus.map(p => p.code).join('|')`) avec un `eslint-disable` : le
     compilateur React refusait alors de mémoïser la fonction entière — trois
     « Compilation Skipped » pour une seule dépendance mal formée. */
  const postes = React.useMemo(() => rhPostes.filter((p) => p.actif !== false), [rhPostes]);

  /* CE QUE CHACUN DÉTIENT, indexé une fois pour toutes : `orgId → code → niveau`.
     Lu dans `rhDetenues` (`ged-habilitation-personne`) et non plus dans
     `settings.habilitations` — cette matrice-là est en cours de retrait, ses
     axes sont des codes de poste dont six sont périmés depuis le renommage
     des paillasses, et elle ne bloquait déjà plus rien au planning. */
  const detenuesParPersonne = React.useMemo(() => {
    const par = new Map<string, Map<string, string>>();
    for (const d of rhDetenues || []) {
      const cle = String(d.orgId || '');
      if (!cle) continue;
      if (!par.has(cle)) par.set(cle, new Map());
      par.get(cle)!.set(String(d.code), String(d.niveau || ''));
    }
    return par;
  }, [rhDetenues]);

  /* Les cases de l'organigramme, pour nommer une fonction en toutes lettres
     dans la phrase qui explique combien de personnes sont masquées. */
  const fonctionsOrg = React.useMemo(
    () => (settings?.orgFonctions || []).map((f) => ({ id: String(f.id), label: String(f.label || f.id) })),
    [settings?.orgFonctions],
  );

  const decalerSemaine = (n: number) => {
    const [a, m, j] = rhSemaine.split('-').map(Number);
    setRhSemaine(lundiDe(new Date(a, m - 1, j + n * 7)));
  };

  const casesDe = (date: string, code: string) =>
    rhAffectations.filter((x) => x.date === date && x.poste?.code === code);

  const derogations = rhAffectations.filter((a) => a.derogation).length;
  const salariesPlanifiables = rhPersonnel.filter((p) => p.type === 'salarie' && p.statut !== 'sorti');

  /**
   * L'état d'ouverture vient du SERVEUR, poste par poste et jour par jour.
   *
   * Il remplace l'heuristique qui ne savait dire que « il y a quelqu'un, mais
   * moins que l'effectif cible » et devait taire le cas le plus grave — le
   * poste ouvert que personne ne tient. Elle le taisait faute de calendrier :
   * rien ne distinguait « personne le dimanche parce que c'est fermé » de
   * « personne le mardi alors qu'il en faut deux ».
   *
   * Un poste sans calendrier rend `indetermine` et ne compte dans rien : la
   * mise en service ne transforme pas d'un coup le silence en anomalie.
   */
  const etatDe = (d: string, code: string) => rhOuvertures.find((o) => o.date === d && o.poste === code);
  const aTraiter = (e?: RhOuverture) => !!e && (e.ecart === 'non_couvert' || e.ecart === 'sous_effectif' || e.ecart === 'hors_ouverture');

  const ecarts = rhOuvertures.filter((o) => jours.includes(o.date) && postes.some((p) => p.code === o.poste));
  const nonCouverts = ecarts.filter((o) => o.ecart === 'non_couvert').length;
  const incomplets = ecarts.filter((o) => o.ecart === 'sous_effectif').length;
  const horsOuverture = ecarts.filter((o) => o.ecart === 'hors_ouverture').length;
  const sansCalendrier = postes.filter((p) => !p.calendrier?.regles?.length).length;

  // Les deux compteurs sont des FILTRES, pas des décorations : cliquer réduit la
  // grille aux lignes concernées. Un chiffre qu'on ne peut pas suivre oblige à
  // le chercher à l'œil sur sept colonnes.
  /* ⚠️ Un poste sans fonction déclarée est ÉCARTÉ quand un filtre est actif,
     jamais montré « au cas où » : le laisser passer ferait croire qu'il relève
     de la fonction choisie. Le compte affiché dit combien sont ainsi mis de
     côté, sans quoi on croirait le filtre cassé. */
  const parFonction = React.useMemo(
    () => (fonctionVue ? postes.filter((p) => (p.fonctions || []).includes(fonctionVue)) : postes),
    [postes, fonctionVue],
  );
  const postesSansFonction = fonctionVue ? postes.filter((p) => !(p.fonctions || []).length).length : 0;

  /* L'aperçu du prévisionnel : durée choisie, projet rendu, cases écartables. */
  /* LA CELLULE DE LA GRILLE SEMAINE, une fois pour les deux sens de lecture.
     La RQ a demandé la transposition en vue Mois le 29/08, puis dans la
     Semaine le 30 : « je le veux également dans l'affichage semaine ».
     Dupliquer le rendu aurait fait diverger les deux vues au premier
     correctif — or c'est ici que se voient la dérogation, le trou de
     couverture et l'affectation en formation. Ils doivent se voir à
     l'identique dans les deux sens. */
  /* ⚠️ LE DÉTAIL DU POSTE, AU SURVOL (RQ, 30/08/2026 : « il y a des petites
     notes sur les postes pour les particularités, je voudrais un i
     d'information qui permet de voir ces détails »).
     Le tableau du laboratoire porte ces notes en clair — « HTO avant 9h »,
     « Bio_mol avant 11h », « Samedi : 9h–15h », « Routine / Bio-mol / STOCK ».
     Elles ne vivaient nulle part dans l'outil : le planning affichait un code
     et taisait ce qui distingue le poste. */
  const JOURS_COURTS_ISO = ['', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
  const detailPoste = (p: RhPoste) => [
    p.libelle,
    p.habilitationsRequises?.length
      ? `Exige : ${p.habilitationsRequises.map((e) => libelleExigence(e, rhHabilitations)).join(', ')}`
      : 'Aucune habilitation exigée',
    p.calendrier?.regles?.length ? p.calendrierResume : 'Ouverture non définie',
    /* Les séances D'ABORD quand il y en a : elles disent les horaires mieux que
       la plage du poste, qui n'est alors qu'un défaut. */
    ...(p.seances?.length
      ? p.seances.map((s) => `• ${s.libelle} ${s.debut}–${s.fin}`
          + (s.effectif > 1 ? ` · ${s.effectif} places` : '')
          + (s.jours?.length ? ` · ${s.jours.map((j) => JOURS_COURTS_ISO[j]).join(', ')}` : '')
          + (s.note ? ` — ${s.note}` : ''))
      : [(p.plageDebut || p.plageFin) ? `Horaires : ${p.plageDebut}–${p.plageFin}` : '']),
    p.note ? `Note : ${p.note}` : '',
    'Cliquer pour la définition complète du poste.',
  ].filter(Boolean).join('\n');

  const celluleSemaine = (p: RhPoste, d: string) => {
    const cases = casesDe(d, p.code);
    const e = etatDe(d, p.code);
    const ferme = e?.ouverture.etat === 'ferme';
    // Un jour fermé reste affectable : un renfort ponctuel existe.
    // Il est seulement montré pour ce qu'il est, au lieu d'être
    // proposé au même titre qu'un jour ouvert.
    return (
      <CaseDeposable key={d} cible={{ posteCode: p.code, date: d }} titre={e?.libelle}
        /* `relative` : le « + » se place en haut à droite DE LA CASE, et il lui
           faut ce repère — sans lui il se calerait sur le tableau entier. */
        /* ⚠️ PLUS DE `paddingTop: 20` (Lot 1, 02/09/2026). Ces vingt pixels
           n'existaient que pour dégager le « + » posé en haut à droite : ils
           étaient réservés sur les 224 cases de la grille, y compris celles où
           le bouton ne s'affiche pas. Le « + » est désormais posé PAR-DESSUS
           (`position: absolute`, sans réservation) et la case ne paie plus sa
           place. Sur sept jours × trente-deux lignes, c'est la moitié de la
           hauteur du tableau. */
        /* ⚠️ LA SÉLECTION SE FAIT AU CLIC, JAMAIS AU GLISSÉ (Lot 5). Le glissé
           est réservé au déplacement d'une affectation : deux gestes qui
           commencent pareil et finissent autrement, c'est une fonction sur deux
           qui se déclenche par erreur.
           Le clic sur la CASE et non sur une affectation : on sélectionne un
           poste-jour, pas une personne. */
        onClick={(ev) => {
          /* Un clic sur une commande de la case (le « + », la corbeille) n'est
             pas un clic de sélection — sinon ajouter quelqu'un sélectionnerait
             la case au passage. */
          if (!ecriture) return;
          if ((ev.target as HTMLElement).closest('button')) return;
          cliquerCase(p.code, d, ev.shiftKey);
        }}
        /* ⚠️ SÉLECTION ET GLISSÉ NE SE MARCHENT PAS DESSUS : le contexte est
           inactif dès qu'une plage est sélectionnée (`actif: modifiable &&
           !plage`), et la contrainte de distance de 8 px fait qu'un clic net
           n'amorce jamais un glissement. */
        style={{
          ...tdStyle,
          padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)',
          position: 'relative', verticalAlign: 'top', cursor: 'default',
          background: dansPlage(p.code, d) ? 'var(--cyan-50)'
            : ferme && !cases.length ? 'var(--gray-25)'
              /* La colonne du JOUR MÊME, teintée de haut en bas (RQ, 01/10/2026 :
                 « un peu plus de mise en évidence »). */
              : d === auj ? 'var(--blue-50)' : undefined,
          ...(d === auj ? { borderInlineStart: '2px solid var(--color-primary-active)', borderInlineEnd: '2px solid var(--color-primary-active)' } : {}),
          /* ⚠️ UN CONTOUR NET, pas seulement un fond teinté : sur une grille qui
             porte déjà des fonds (jour fermé, jour même, écart), un changement
             de fond de plus se confond avec eux. Le trait, lui, ne ressemble à
             rien d'autre à l'écran. */
          outline: dansPlage(p.code, d) ? '2px solid var(--color-primary)' : undefined,
          outlineOffset: -2,
        }}>
        {ferme && (
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', color: e?.ecart === 'hors_ouverture' ? 'var(--amber-600)' : 'var(--text-faint)', marginBottom: cases.length ? 4 : 2 }}>
            {e?.ecart === 'hors_ouverture' ? 'HORS OUVERTURE' : 'FERMÉ'}
          </div>
        )}
        {cases.map((a) => (
          <AffectationGlissable key={a.documentId} a={{
            documentId: a.documentId, orgId: a.personnel?.orgId || '',
            nom: prenomDe(a.personnel) || nomDe(a.personnel) || '?',
            posteCode: p.code, date: d,
          }}>
            <Case a={a} onRetirer={retirerAffectation} modifiable={ecriture}
              couleur={couleurDe(a.personnel?.orgId || '')} />
          </AffectationGlissable>
        ))}
        {/* ⚠️ TAILLE ET INTERLIGNE RELEVÉS EN COMPACT (13/09/2026). L'heure de
            la case, juste en dessous, occupait cette place jusqu'ici et cadrait
            l'œil ; masquée en compact (`compact` ci-dessous), le mot restait à
            la même taille qu'en confortable et se lisait tassé contre le nom.
            La couleur et le texte ne changent pas — seule la lecture change. */}
        {e?.ecart === 'non_couvert' && (
          <div style={{ fontSize: 13, lineHeight: 1.4, fontWeight: 700, color: 'var(--red-600)', marginBottom: 3 }}>
            personne — {e.ouverture.effectifAttendu} attendu(s)
          </div>
        )}
        {e?.ecart === 'sous_effectif' && (
          <div style={{ fontSize: 13, lineHeight: 1.4, fontWeight: 700, color: 'var(--amber-600)', margin: '3px 0' }}>
            {e.planifies}/{e.ouverture.effectifAttendu} — il en manque {e.manque}
          </div>
        )}
        {e?.ecart === 'surnombre' && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '3px 0' }}>
            {e.planifies} pour {e.ouverture.effectifAttendu} attendu(s)
          </div>
        )}
        {/* ⚠️ LE « + » EN PETIT (RQ, 30/08/2026). Il occupait toute la largeur
            de la case, sous les noms : sur sept colonnes il pesait autant que le
            planning lui-même et l'œil s'y arrêtait avant de lire qui travaille.
            ⚠️ MAIS il garde sa couleur d'écart : rouge quand la case n'est pas
            couverte, ambre quand elle est incomplète. Le réduire ne doit pas
            faire disparaître le seul signal qui appelle une action — c'est
            précisément ce que cette vue existe pour montrer.

            ⚠️ EN BAS À DROITE DEPUIS LE 02/09/2026, et non plus en haut. Les
            vingt pixels de `padding-top` qui lui étaient réservés ont disparu
            (Lot 1) : posé en haut, il serait tombé exactement sur la corbeille
            de la première affectation. En bas, il occupe l'espace libre — les
            affectations s'empilent depuis le haut — et dans une case saturée il
            se pose sur le coin bas droit de la dernière, qui ne porte aucune
            commande.

            ⚠️ TOUJOURS VISIBLE, jamais au seul survol : le planning se consulte
            sur tablette, où il n'y a pas de survol. Une commande qui n'apparaît
            qu'au pointeur y devient inatteignable. */}
        {ecriture && (
        <button type="button"
          onClick={() => setCible({ date: d, poste: p })}
          className="no-print"
          style={{
            position: 'absolute', bottom: 2, insetInlineEnd: 2,
            width: 18, height: 18, lineHeight: '16px', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 'var(--radius-xs)', cursor: 'pointer', fontSize: 14, fontWeight: 700,
            background: 'transparent',
            border: `1px dashed ${e?.ecart === 'non_couvert' ? 'var(--red-600)'
              : e?.ecart === 'sous_effectif' ? 'var(--amber-600)' : 'var(--border-default)'}`,
            color: e?.ecart === 'non_couvert' ? 'var(--red-600)'
              : e?.ecart === 'sous_effectif' ? 'var(--amber-600)'
              : 'var(--text-muted)',
          }}
          title={e?.ecart === 'sous_effectif' ? 'Compléter cette case' : 'Affecter quelqu’un'}
          aria-label={`Affecter quelqu’un à ${p.libelle} le ${d}${e ? ` — ${e.libelle}` : ''}`}
        >
          +
        </button>
        )}
      </CaseDeposable>
    );
  };

  const [jourAPropager, setJourAPropager] = React.useState('');
  /* ── DEUX ONGLETS : EN COURS / PRÉVISIONNEL (RQ, 30/09/2026) ────────────
     « Je voudrais deux onglets, prévisionnel et en cours ; le prévisionnel sur
     fond gris, pour savoir qu'on ne modifie pas le planning en cours. » L'en
     cours est la grille qu'on modifie ; le prévisionnel ne montre que l'aperçu
     calculé, ses règles, ses dates et le compteur d'équilibre — rien n'y est
     écrit avant « Valider et écrire ». */
  /* ── L'ONGLET « CONVENTIONS » (Inès, 05/10/2026) ────────────────────────
   *
   * « Peux-tu rajouter un onglet après les échanges pour les conventions au
   *   lieu de l'endroit de maintenant ? »
   *
   * Le calendrier des conventions vivait à une adresse à lui
   * (`/calendrier`), HORS de la barre latérale : on ne le trouvait qu'en le
   * cherchant dans la palette. Il vient ici, à côté du planning qu'il annote —
   * c'est la même question (« qui passe quand »), et les repères colorés de la
   * grille n'avaient aucun endroit évident où se faire expliquer.
   *
   * ⚠️ L'ADRESSE `/calendrier` EST CONSERVÉE ET REDIRIGE (voir `page.tsx`) :
   * un slug publié est un contrat, et la RQ a déjà envoyé ce lien (« voilà les
   * passages du mois »). Un signet qui tombe en page blanche se lit comme une
   * panne. */
  type OngletPlanning = 'encours' | 'previsionnel' | 'echanges' | 'conventions';
  const [ongletMemorise, setOngletBrut] = React.useState<OngletPlanning>(() => {
    try {
      const v = localStorage.getItem('bio24.planning.onglet');
      return v === 'previsionnel' || v === 'echanges' || v === 'conventions' ? v : 'encours';
    } catch { return 'encours'; }
  });
  /* Le prévisionnel est réservé au rôle `rh` (RQ, 03/10/2026). L'onglet gardé
     en mémoire du navigateur ne doit jamais rouvrir ce que la session n'a plus
     le droit de voir. */
  const peutPrevisionnel = peutVoirPrevisionnel(session.sections);
  /* L'onglet ÉCHANGES (RQ, 04/10/2026) : toutes les demandes d'échange — en attente,
     approbation, refus, historique — au rôle `rh`, qui seul les décide. */
  const estRh = (session.roles || []).includes('rh');
  /* Le nombre de demandes d'échange EN ATTENTE, porté par l'onglet (RQ, 04/10/2026) :
     on le voit sans ouvrir l'onglet. Relu après chaque décision (`revEchanges`). */
  const [nbEchangesAttente, setNbEchangesAttente] = React.useState(0);
  const [revEchanges, setRevEchanges] = React.useState(0);
  React.useEffect(() => {
    if (!estRh) return undefined;
    let vivant = true;
    void fetch('/api/ged/ged/rh/echanges', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { echanges?: { statut: string }[] } | null) => { if (vivant && d) setNbEchangesAttente((d.echanges || []).filter((e) => e.statut === 'demandee').length); })
      .catch(() => { /* compteur indisponible : l'onglet reste sans pastille */ });
    return () => { vivant = false; };
  }, [estRh, revEchanges]);
  const onglet: OngletPlanning = ongletMemorise === 'previsionnel' && peutPrevisionnel ? 'previsionnel'
    : ongletMemorise === 'echanges' && estRh ? 'echanges'
      /* Pas de garde propre : qui ouvre cet écran lit déjà le planning, et le
         calendrier borne lui-même l'écriture (`sections.planning.modifier`). */
      : ongletMemorise === 'conventions' ? 'conventions' : 'encours';
  const setOnglet = (o: OngletPlanning) => { setOngletBrut(o); try { localStorage.setItem('bio24.planning.onglet', o); } catch { /* sans stockage */ } };
  /* Les dates à prévoir : par défaut, la semaine qui suit, sur 8 semaines. */
  const [duPrev, setDuPrev] = React.useState(() => {
    const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
    return fmtISO(d);
  });
  const [auPrev, setAuPrev] = React.useState(() => {
    const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7) + 8 * 7 - 1);
    return fmtISO(d);
  });
  const [genEnCours, setGenEnCours] = React.useState(false);
  const [apercu, setApercu] = React.useState<{
    du: string; au: string; aCreer: number;
    equilibre?: { semaines: number; regleActive: boolean; compteurs: { orgId: string; nom: string; historique: number; projete: number }[] }; projet: { date: string; poste: string; posteLibelle: string; orgId: string; nom: string; derogation?: boolean; raisons?: string[]; formation?: boolean }[];
    vides: { date: string; poste: string; motif: string }[]; ecartees: Set<string>;
    /** Règles qui n'ont PAS pu être tenues — la case est remplie quand même. */
    manquements?: { date: string; poste: string; motif: string }[];
    /** Les dépassements du plafond de dimanches/fériés par mois (01/10/2026). */
    depassementsDimFer?: { orgId: string; nom: string; mois: string; nombre: number; plafond: number }[];
    /** Les semaines de sept jours (plafond retiré le 01/10/2026) — à part. */
    semainesSansRepos?: { date: string; nom: string; motif: string }[];
  } | null>(null);
  /* ── LE PRÉVISIONNEL MOIS PAR MOIS (RQ, 01/10/2026) ─────────────────────
     « Présente l'outil pour le prochain mois non prévu. » À l'ouverture de
     l'onglet, on cherche le premier mois à venir sans aucune affectation de
     travail ; les dates du calcul suivent le mois choisi. */
  const [moisPrev, setMoisPrevBrut] = React.useState<string | null>(null);
  const [dejaPlanifie, setDejaPlanifie] = React.useState<{ date: string; orgId: string; poste: string; heureDebut?: string | null; heureFin?: string | null }[]>([]);
  /** Le planning réel des 12 mois qui précèdent le mois prévu — décomptes O/G/D-F. */
  const rhFeriesPlanning = useGed((st) => st.rhFeries);
  const chomes = React.useMemo(() => new Set(rhFeriesPlanning.map((f) => f.date)), [rhFeriesPlanning]);
  const bornesMois = (m: string) => {
    const [a, mm] = m.split('-').map(Number);
    return { du: `${m}-01`, au: `${m}-${String(new Date(a!, mm!, 0).getDate()).padStart(2, '0')}` };
  };
  const decalerMoisPrev = (m: string, n: number) => { const d = new Date(`${m}-15T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
  const lirePlage = React.useCallback(async (du: string, au: string) => {
    const r = await fetch(`/api/ged/ged/rh/affectations?du=${du}&au=${au}`, { credentials: 'include' });
    const d = r.ok ? await r.json() : {};
    const l = (d.affectations || d.data || []) as RhAffectation[];
    return l.filter((a) => a.nature !== 'absence' && a.personnel?.orgId)
      .map((a) => ({ date: String(a.date), orgId: String(a.personnel?.orgId), poste: String(a.poste?.code || ''),
        heureDebut: a.heureDebut || a.poste?.plageDebut || null, heureFin: a.heureFin || a.poste?.plageFin || null }));
  }, []);
  const lireMois = React.useCallback(async (m: string) => { const { du, au } = bornesMois(m); return lirePlage(du, au); }, [lirePlage]);
  /* ── LE TRIMESTRE CIVIL (RQ, 01/10/2026 : « générer les plannings de façon
     trimestrielle — trimestres civils, validation mois par mois ») ─────────
     `moisPrev` est le PREMIER mois du trimestre. Le calcul couvre les trois
     mois d'un coup (chaque mois s'enchaîne sur le précédent : rotation,
     formation, tour de garde, dimanches) ; la validation se fait mois par
     mois. Le calcul part au plus tôt DEMAIN : le passé n'est jamais rempli. */
  const debutTrim = (m: string) => { const [a, mm] = m.split('-').map(Number); return `${a}-${String(Math.floor((mm! - 1) / 3) * 3 + 1).padStart(2, '0')}`; };
  const moisDuTrim = (q: string) => [q, decalerMoisPrev(q, 1), decalerMoisPrev(q, 2)];
  const [moisActif, setMoisActif] = React.useState<string | null>(null);
  const choisirMoisPrev = React.useCallback(async (q0: string) => {
    const q = debutTrim(q0);
    setMoisPrevBrut(q); setApercu(null);
    const demain = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return fmtISO(d); })();
    const du = `${q}-01` < demain ? demain : `${q}-01`;
    const au = bornesMois(decalerMoisPrev(q, 2)).au;
    setDuPrev(du); setAuPrev(au);
    setMoisActif(moisDuTrim(q).find((m) => bornesMois(m).au >= du) || q);
    setDejaPlanifie(await lirePlage(du, au));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- aides pures, recréées à chaque rendu
  }, [lirePlage]);
  /* ── LA VUE D'UN MOIS DU TRIMESTRE ─────────────────────────────────────
     Le calcul couvre le trimestre ; l'écran montre UN mois à la fois (onglets),
     avec ses propres décomptes. « D/F avant » = le réel non réalisé (serveur)
     + ce qui est déjà planifié ou prévu dans les mois PRÉCÉDENTS du trimestre. */
  const HORS_DF = new Set(['garde', 'validation', 'services']);
  const catPoste = (code: string) => rhPostes.find((x) => x.code === code)?.categorie || '';
  const compterDF = (l: { date: string; orgId: string; poste: string; heureDebut?: string | null; heureFin?: string | null }[]) => {
    const m = new Map<string, Set<string>>();
    for (const t of l) {
      if (HORS_DF.has(catPoste(t.poste))) continue;
      if (!(chomes.has(t.date) || new Date(`${t.date}T12:00:00`).getDay() === 0)) continue;
      const k = t.orgId; (m.get(k) || m.set(k, new Set()).get(k)!).add(`${t.date}|${t.heureDebut || ''}-${t.heureFin || ''}`);
    }
    return new Map([...m].map(([k, v]) => [k, v.size]));
  };
  const vueMois = (m: string) => {
    const b = bornesMois(m);
    const du = b.du < duPrev ? duPrev : b.du;
    const dans = (d: string) => d >= du && d <= b.au;
    const projet = apercu ? apercu.projet.filter((x) => dans(x.date)) : null;
    const vides = apercu ? apercu.vides.filter((x) => dans(x.date)) : [];
    const deja = dejaPlanifie.filter((x) => dans(x.date));
    /* LA VEILLE DU PREMIER JOUR (RQ, 03/10/2026 : « Abderraouf est en PG le 1er
       novembre, non ? ») : sa garde du 31 octobre est HORS du mois affiché.
       Sans elle, le post garde du 1er ne pouvait pas se voir. Elle ne sert qu'à
       ça — jamais aux décomptes du mois. */
    const veilleDu = (() => { const x = new Date(`${du}T12:00:00Z`); x.setUTCDate(x.getUTCDate() - 1); return isoJour(x); })();
    const veille = [
      ...dejaPlanifie.filter((x) => x.date === veilleDu),
      ...(apercu ? apercu.projet.filter((x) => x.date === veilleDu && !apercu.ecartees.has(`${x.date}|${x.poste}|${x.orgId}`)) : []),
    ].map((x) => ({ orgId: x.orgId, poste: x.poste }));
    const plage = (code: string) => rhPostes.find((x) => x.code === code);
    /* « D/F TRIMESTRE » (RQ, 01/10/2026 : « au lieu de D/F avant, D/F
       trimestre, c'est-à-dire les 3 mois du trimestre ») : planning réel du
       trimestre + tout le prévu des trois mois (hors cases écartées). */
    const prevusTrim = apercu ? apercu.projet.filter((x) => !x.formation && !apercu.ecartees.has(`${x.date}|${x.poste}|${x.orgId}`))
      .map((x) => ({ date: x.date, orgId: x.orgId, poste: x.poste, heureDebut: plage(x.poste)?.plageDebut, heureFin: plage(x.poste)?.plageFin })) : [];
    const dfAvant = compterDF([...dejaPlanifie, ...prevusTrim]);
    return { du, au: b.au, projet, vides, deja, dfAvant, veille };
  };
  React.useEffect(() => {
    if (onglet !== 'previsionnel' || moisPrev) return;
    void (async () => {
      let m = decalerMoisPrev(fmtISO(new Date()).slice(0, 7), 1);
      for (let i = 0; i < 12; i += 1) {
        if (!(await lireMois(m)).length) break;
        m = decalerMoisPrev(m, 1);
      }
      /* Le TRIMESTRE du premier mois non prévu (transition : octobre-décembre). */
      await choisirMoisPrev(debutTrim(m));
    })();
  }, [onglet, moisPrev, lireMois, choisirMoisPrev]);
  /* ── LE LOT DE LA DERNIÈRE GÉNÉRATION (13/09/2026) ────────────────────────
     Il n'existe qu'après l'écriture, et c'est lui qui rend l'annulation
     possible. Gardé en mémoire d'écran seulement : au rechargement, le moteur
     retrouve le dernier lot en base — on ne perd donc pas la possibilité
     d'annuler, on perd seulement le bouton, et le menu d'actions le rend. */
  const [dernierLot, setDernierLot] = React.useState<string | null>(null);
  /** Le panneau des règles d'affectation, ouvert depuis le prévisionnel — un
   *  second accès à `ReglesAffectation`, qui reste par ailleurs joignable en
   *  plein écran par la barre (`reglesplanning`) : voir la note plus haut. */
  const [reglesOuvertes, setReglesOuvertes] = React.useState(false);
  /** L'aperçu rendu en grille (comme la vue Mois) plutôt qu'en seule liste à
   *  cocher — la liste reste en dessous, c'est elle qui permet toujours
   *  d'écarter une case précise avant validation (RQ, 13/09/2026). */
  /* La liste est remplacée par la vue par poste (RQ, 01/10/2026). */
  const [apercuEnGrille] = React.useState(true);

  const postesVus = React.useMemo(
    () => (focus === 'derogations'
      ? parFonction.filter((p) => jours.some((d) => casesDe(d, p.code).some((a) => a.derogation)))
      : focus === 'ecarts'
        ? parFonction.filter((p) => jours.some((d) => aTraiter(etatDe(d, p.code))))
        : parFonction),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parFonction, focus, jours, rhAffectations, rhOuvertures],
  );

  const gensVus = focus === 'derogations'
    ? salariesPlanifiables.filter((p) => rhAffectations.some((a) => a.personnel?.orgId === p.orgId && a.derogation))
    : salariesPlanifiables;
  const groupesGens = FAMILLES_PERSONNEL.map((f) => ({
    cle: f.cle, titre: f.titre,
    personnes: gensVus.filter((p) => familleDe(p) === f.cle).sort((a, b) => sansTitre(nomDe(a)).localeCompare(sansTitre(nomDe(b)), 'fr')),
  })).filter((g) => g.personnes.length);

  /* LES BLOCS (RQ, 24/08/2026) — accueil, technique, validation biologique,
     qualité, entretien. La grille rendait une liste plate, où l'accueil, la
     garde et le coursier se suivaient sans rien qui les distingue. `grouperLignes`
     travaille sur la CATÉGORIE du poste depuis le 30/08 — plus sur une liste de
     codes nommés à la main, qui ignorait tout poste créé après elle.
     ⚠️ Les blocs VIDES ne sortent pas — le filtre « écarts » peut ne laisser
     qu'une paillasse, et cinq intitulés sans ligne dessous se liraient comme
     cinq postes non pourvus. */
  const blocsPostes = React.useMemo(() => {
    const par = new Map(postesVus.map((p) => [p.code, p]));
    return grouperLignes(postesVus.map((p) => ({ cle: p.code, titre: p.libelle, sous: p.code, categorie: p.categorie })))
      .map((g) => ({ ...g, postes: g.lignes.map((l) => par.get(l.cle)!).filter(Boolean) }));
  }, [postesVus]);

  /* Les fonctions RÉELLEMENT portées par un poste — pas les dix-huit cases de
     l'organigramme. Offrir un filtre qui ne rend rien se lit comme un bug. */
  const fonctionsDeclarees = React.useMemo(() => {
    const vues = new Set(postes.flatMap((p) => p.fonctions || []));
    return ((settings?.orgFonctions || []) as { id: string; label: string }[])
      .filter((f) => vues.has(f.id));
  }, [postes, settings?.orgFonctions]);

  const basculerFocus = (f: 'derogations' | 'ecarts') => setFocus((x) => (x === f ? null : f));

  /* La confirmation de la maison : une promesse, une fenêtre, pas de
     `window.confirm` — que le navigateur intégré refuse. */
  const { demander, confirmation } = useConfirmation();
  /* Le compte des filtres actifs a quitté le bouton (04/10/2026) : la fonction affichée
     se lit désormais en clair dans la barre ; seul « passages » reste dans le menu. */

  /* ── LARGEURS DE COLONNES (Lot 4, 02/09/2026) ────────────────────────────
   * `table-layout: fixed` reste — c'est ce qui tient les performances sur
   * trente-deux lignes — donc les largeurs sont un ÉTAT, jamais une conséquence
   * du contenu. Elles se retrouvent d'une visite à l'autre.
   * La clé d'une colonne de jour est son RANG (`j0`…`j6`) et non sa date : on
   * règle « la colonne du lundi », pas « la colonne du 31 août ». Une clé datée
   * remettrait tout à zéro chaque semaine. */
  const [largeurs, setLargeurs] = React.useState<Largeurs>(() => lireLargeurs());
  const poserLargeur = (cle: string, px: number) => setLargeurs((l) => {
    const suivant = { ...l, [cle]: borner(px) };
    ecrireLargeurs(suivant);
    return suivant;
  });
  const reinitialiserLargeurs = () => { oublierLargeurs(); setLargeurs({}); };
  /** Largeur effective : réglée, sinon celle qui vaut aujourd'hui. */
  const largeurDe = (cle: string, defaut: number) => largeurs[cle] ?? defaut;
  /* « Élargir ce jour » : on double la colonne visée sans toucher aux autres.
     Le tableau défile — c'est le comportement voulu, inspecter une journée ne
     doit pas écraser les six autres. */
  const elargirJour = (i: number) => poserLargeur(`j${i}`, largeurDe(`j${i}`, 110) * 2);

  /* ── SÉLECTION DE PLAGE (Lot 5, 02/09/2026) ──────────────────────────────
   * CLIC puis MAJ+CLIC, jamais le glissé : le glissé est réservé au
   * déplacement d'affectation (Lot 6). Décidé maintenant, comme la spec
   * l'impose — deux gestes qui commencent pareil et finissent autrement, c'est
   * une fonction sur deux qui se déclenche par erreur.
   *
   * L'ancre est la première case cliquée, l'extrémité la seconde. Le rectangle
   * est calculé au rendu : le stocker le ferait vieillir dès qu'un filtre
   * change la liste des postes affichés. */
  const [ancre, setAncre] = React.useState<{ poste: string; date: string } | null>(null);
  const [bout, setBout] = React.useState<{ poste: string; date: string } | null>(null);
  const [enCoursPlage, setEnCoursPlage] = React.useState(false);
  /** Ce que le dernier vidage a supprimé, et jusqu'à quand on peut l'annuler. */
  const [annulable, setAnnulable] = React.useState<{ contenu: unknown[]; jusqua: number } | null>(null);

  useEscape(!!ancre, () => { setAncre(null); setBout(null); });

  /** Le rectangle poste × jours entre l'ancre et l'extrémité. */
  const plage = React.useMemo(() => {
    if (!ancre) return null;
    const b2 = bout || ancre;
    const codes = postesVus.map((p) => p.code);
    const i1 = codes.indexOf(ancre.poste); const i2 = codes.indexOf(b2.poste);
    const j1 = jours.indexOf(ancre.date); const j2 = jours.indexOf(b2.date);
    /* Une extrémité hors de la vue (un filtre l'a masquée) : on ne devine pas,
       on retombe sur la seule case sûre — l'ancre. */
    if (i1 < 0 || i2 < 0 || j1 < 0 || j2 < 0) return { postes: [ancre.poste], dates: [ancre.date] };
    return {
      postes: codes.slice(Math.min(i1, i2), Math.max(i1, i2) + 1),
      dates: jours.slice(Math.min(j1, j2), Math.max(j1, j2) + 1),
    };
  }, [ancre, bout, postesVus, jours]);

  /** Les affectations RÉELLEMENT dans la plage — le nombre annoncé avant
   *  confirmation, et celui que le serveur revérifie. */
  const affectationsPlage = React.useMemo(() => {
    if (!plage) return [];
    const dp = new Set(plage.postes); const dd = new Set(plage.dates);
    return rhAffectations.filter((a) => dd.has(a.date) && dp.has(a.poste?.code || ''));
  }, [plage, rhAffectations]);

  const dansPlage = (posteCode: string, date: string) =>
    !!plage && plage.postes.includes(posteCode) && plage.dates.includes(date);

  const cliquerCase = (posteCode: string, date: string, avecMaj: boolean) => {
    if (avecMaj && ancre) { setBout({ poste: posteCode, date }); return; }
    setAncre({ poste: posteCode, date }); setBout(null);
  };
  const viderSelection = () => { setAncre(null); setBout(null); };
  /* Fermer « Modifier » ferme aussi la sélection : sélectionner une plage ne sert
     qu'à la dupliquer ou la vider, c'est-à-dire à écrire. */
  React.useEffect(() => { if (!ecriture) { setAncre(null); setBout(null); } }, [ecriture]);

  /**
   * RECHARGER APRÈS UNE ÉCRITURE DE PLANNING.
   *
   * ⚠️ `loadRhGestion` ne recharge PAS les affectations — il ne lit que les
   * paramètres, les absences, les fériés, les soldes et les régimes. Les six
   * actions qui écrivent le planning l'appelaient pourtant, si bien qu'aucune
   * ne rafraîchissait les cases : après « Vider », la case supprimée restait à
   * l'écran jusqu'à ce qu'on change de semaine et qu'on revienne (constat de la
   * RQ, 03/09/2026). Les autres actions souffraient du même défaut en sens
   * inverse — les cases CRÉÉES n'apparaissaient pas, ce qui se lit « ça n'a pas
   * marché ».
   *
   * ⚠️ Et `loadMaintenances` avec, pour la raison déjà réglée le 28/08 sur
   * `affecter` : le destinataire d'une maintenance préventive est la personne
   * planifiée ce jour-là. Vider une plage laisse donc la projection réclamer un
   * acte à quelqu'un qui n'y est plus.
   */
  const rechargerPlanning = React.useCallback(async () => {
    await Promise.all([loadRh(), loadMaintenances()]);
  }, [loadRh, loadMaintenances]);


  /* ── GLISSER-DÉPOSER (Lot 6, 02/09/2026) ─────────────────────────────────
   * ⚠️ LA VALIDATION EST LOCALE ET PENDANT LE GLISSÉ — c'est le point central.
   * Elle réutilise `verdictLocal`, la MÊME fonction que le dialogue
   * d'affectation, jamais une seconde règle : deux implémentations de « qui
   * peut tenir ce poste » divergent, et c'est le chemin le plus rapide — le
   * glissé — qui finirait par laisser passer ce que l'autre refuse.
   * Le SERVEUR revérifie tout : ceci ne fait qu'éviter un geste voué au refus. */
  const contexteDnd = React.useMemo(() => ({
    actif: ecriture && !plage,
    valide: (a: AffectationGlissee, c: CibleCase) => {
      const poste = postes.find((p) => p.code === c.posteCode);
      const pers = rhPersonnel.find((p) => p.orgId === a.orgId);
      if (!poste || !pers) return { ok: false, motif: 'Poste ou personne inconnus.' };
      /* Une case DÉJÀ OCCUPÉE par cette personne : refus. Le déplacement
         créerait un doublon, pas un déplacement. */
      if (rhAffectations.some((x) => x.date === c.date && x.poste?.code === c.posteCode && x.personnel?.orgId === a.orgId)) {
        return { ok: false, motif: 'Déjà planifiée sur cette case.' };
      }
      /* Une personne déjà placée AILLEURS ce jour-là : refus. C'est la règle du
         planning — un poste par jour et par personne. */
      if (rhAffectations.some((x) => x.date === c.date && x.personnel?.orgId === a.orgId && x.documentId !== a.documentId)) {
        return { ok: false, motif: 'Déjà sur un autre poste ce jour-là.' };
      }
      const e = etatDe(c.date, c.posteCode);
      if (e?.ouverture.etat === 'ferme') return { ok: false, motif: 'Le poste n’ouvre pas ce jour-là.' };
      const v = verdictLocal(pers, poste, detenuesParPersonne);
      if (!v.ok) return { ok: false, motif: 'Habilitation manquante — passer par le dialogue, qui permet la dérogation.' };
      return { ok: true };
    },
    /* ⚠️ LES DEUX ROUTES QUI EXISTENT, dans cet ordre : on CRÉE avant de
       RETIRER. L'inverse laisserait, si la création échoue, une affectation
       supprimée et rien à sa place — un trou que personne n'a voulu.
       Chacune pose déjà sa trace au journal : c'est précisément pour cela qu'on
       ne les contourne pas. */
    deplacer: async (a: AffectationGlissee, c: CibleCase) => {
      const cree = await affecter({ date: c.date, posteCode: c.posteCode, personnelOrgId: a.orgId });
      if (!cree) return false;
      const retire = await retirerAffectation(a.documentId);
      if (!retire) {
        showToast(
          'La nouvelle affectation est posée, mais l’ancienne n’a pas pu être retirée : '
          + `${a.nom} figure deux fois. Retirez la case du ${fmtJour(a.date)} à la main.`,
          'erreur',
        );
      }
      await rechargerPlanning();
      return true;
    },
  }), [ecriture, plage, postes, rhPersonnel, rhAffectations, detenuesParPersonne, affecter, retirerAffectation, rechargerPlanning, showToast, etatDe]);

  const viderLaPlage = async () => {
    if (!plage) return;
    const n = affectationsPlage.length;
    const ok = await demander(
      `Supprimer ${n} affectation${n > 1 ? 's' : ''} ?\n\n`
      + `Plage : ${plage.postes.length} poste${plage.postes.length > 1 ? 's' : ''}, `
      + `du ${fmtJour(plage.dates[0])} au ${fmtJour(plage.dates[plage.dates.length - 1])}.\n`
      + `${affectationsPlage.filter((a) => a.derogation).length
        ? `Dont ${affectationsPlage.filter((a) => a.derogation).length} dérogation(s) : elles seront retirées, et nommées au journal.\n` : ''}`
      + '\nRien n’est perdu tout de suite : vous pourrez annuler pendant dix secondes.',
      { titre: 'Vider la plage', confirmer: `Supprimer ${n}`, danger: true },
    );
    if (!ok) return;
    setEnCoursPlage(true);
    const r = await fetchEcriture('/api/ged/ged/rh/planning/plage/vider', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      /* `attendu` : le serveur REFUSE si la plage a bougé depuis l'affichage.
         Supprimer « ce qu'il y a maintenant » détruirait ce que personne n'a vu. */
      body: JSON.stringify({ du: plage.dates[0], au: plage.dates[plage.dates.length - 1], postes: plage.postes, attendu: n }),
    });
    const d = await r.json().catch(() => ({}));
    setEnCoursPlage(false);
    if (!r.ok) { showToast(d?.error?.message || 'Suppression refusée.', 'erreur'); return; }
    showToast(`${d.supprimees} affectation(s) supprimée(s).`);
    if (d.supprimees) setAnnulable({ contenu: d.contenu || [], jusqua: Date.now() + 10000 });
    viderSelection();
    await rechargerPlanning();
  };

  const annulerLeVidage = async () => {
    if (!annulable) return;
    const r = await fetchEcriture('/api/ged/ged/rh/planning/plage/restaurer', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contenu: annulable.contenu }),
    });
    const d = await r.json().catch(() => ({}));
    setAnnulable(null);
    if (!r.ok) { showToast(d?.error?.message || 'Annulation impossible.', 'erreur'); return; }
    showToast(`${d.remises} affectation(s) remise(s)${d.sautees?.length ? ` · ${d.sautees.length} non remise(s)` : ''}.`);
    await rechargerPlanning();
  };

  const dupliquerLaPlage = async (semaines: number, conflits: 'refuser' | 'ignorer' | 'ecraser') => {
    if (!plage) return;
    setEnCoursPlage(true);
    const r = await fetchEcriture('/api/ged/ged/rh/planning/plage/dupliquer', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        du: plage.dates[0], au: plage.dates[plage.dates.length - 1],
        postes: plage.postes, decalageJours: semaines * 7, conflits,
      }),
    });
    const d = await r.json().catch(() => ({}));
    setEnCoursPlage(false);
    if (!r.ok) {
      /* Un refus pour CONFLIT n'est pas une erreur : c'est la question posée.
         On la repose telle quelle, avec les deux issues. */
      const msg = String(d?.error?.message || '');
      if (msg.includes('déjà occupées')) {
        const choix = await demander(
          `${msg}\n\nQue faire des cases occupées ?`,
          { titre: 'Cases déjà occupées', confirmer: 'Écraser ce qui s’y trouve' },
        );
        if (choix) await dupliquerLaPlage(semaines, 'ecraser');
        else showToast('Duplication abandonnée — rien n’a été copié.');
        return;
      }
      showToast(msg || 'Duplication refusée.', 'erreur');
      return;
    }
    /* ⚠️ LES REFUS SONT LISTÉS, jamais tus : une copie partielle silencieuse
       fait croire le planning fait alors qu'il manque trois personnes. */
    showToast(`${d.creees} affectation(s) copiée(s)${d.refusees?.length
      ? ` · ${d.refusees.length} refusée(s) : ${d.refusees.slice(0, 2).join(' · ')}${d.refusees.length > 2 ? '…' : ''}`
      : ''}.`, d.refusees?.length ? 'erreur' : undefined);
    viderSelection();
    await rechargerPlanning();
  };

  /* ── LES ACTIONS QUI ÉCRIVENT (Lot 3, 02/09/2026) ────────────────────────
   * Extraites du JSX où elles étaient écrites en ligne, dans des boutons
   * dispersés sur trois rangs. Elles sont désormais appelées par le menu
   * « Actions », derrière une confirmation — même corps, même route, même
   * message : rien de leur comportement ne change ici, seule leur PLACE change.
   * Les extraire est ce qui rend possible de les regrouper sans les récrire. */
  const recopierSemainePrecedente = async () => {
    const [a2, m2, j2] = rhSemaine.split('-').map(Number);
    const depuis = lundiDe(new Date(a2, m2 - 1, j2 - 7));
    const r = await fetchEcriture('/api/ged/ged/rh/planning/dupliquer', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ depuis, vers: rhSemaine }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { showToast(d?.error?.message || 'Duplication refusée.', 'erreur'); return; }
    showToast(`${d.creees} affectation(s) recopiée(s)${d.sautees?.length ? ` · ${d.sautees.length} sautée(s) : ${d.sautees.slice(0, 3).join(' · ')}${d.sautees.length > 3 ? '…' : ''}` : ''}.`);
    await rechargerPlanning();
  };

  const propagerLeJour = async (jour: string) => {
    const r = await fetchEcriture('/api/ged/ged/rh/planning/propager', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jour }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { showToast(d?.error?.message || 'Propagation refusée.', 'erreur'); return; }
    showToast(`${d.creees} affectation(s) créée(s)${d.sautees?.length ? ` · ${d.sautees.length} écartée(s) : ${d.sautees.slice(0, 2).join(' · ')}${d.sautees.length > 2 ? '…' : ''}` : ''}.`);
    setJourAPropager('');
    await rechargerPlanning();
  };

  const calculerApercu = async () => {
    setGenEnCours(true);
    const du = duPrev; const au = auPrev;
    if (!du || !au || au < du) { setGenEnCours(false); showToast('Choisissez des dates à prévoir (du ≤ au).', 'erreur'); return; }
    const r = await fetchEcriture('/api/ged/ged/rh/planning/generer', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ du, au, essai: true }),
    });
    const d = await r.json().catch(() => ({}));
    setGenEnCours(false);
    if (!r.ok) { showToast(d?.error?.message || 'Aperçu impossible.', 'erreur'); return; }
    /* Toutes les cases sont RETENUES par défaut : l'aperçu sert à écarter, pas
       à tout resélectionner. */
    setApercu({ ...d, du, au, ecartees: new Set<string>() });
  };

  /* ── LA GRILLE DE L'APERÇU, COMME LA VUE MOIS (13/09/2026) ────────────────
   * « Je veux qu'on me le montre comme l'affichage du mois » — la liste à
   * cocher, ligne par ligne, ne dit pas d'un coup d'œil OÙ sont les trous sur
   * la durée choisie ; c'est exactement ce que `PlanningMois` sait montrer.
   * On ne réutilise pas ce composant tel quel : il lit `rhAffectationsMois`,
   * chargé depuis le serveur, alors que l'aperçu est un PROJET qui n'existe
   * qu'en mémoire d'écran. La grille ci-dessous reprend seulement sa manière
   * de lire — lignes = postes, colonnes = jours, pastille = personne — sur les
   * données de `apercu.projet` et `apercu.vides`.
   * ⚠️ Mémoïsée avant le retour anticipé (`peutLire`, ci-dessous) : voir la
   * note qui l'explique — aucun crochet ne peut suivre un retour. */
  const grilleApercu = React.useMemo(() => {
    if (!apercu) return null;
    const dates: string[] = [];
    for (let d = new Date(`${apercu.du}T00:00:00`); fmtISO(d) <= apercu.au; d.setDate(d.getDate() + 1)) {
      dates.push(fmtISO(d));
    }
    const postesVues = new Map<string, string>();
    for (const x of apercu.projet) postesVues.set(x.poste, x.posteLibelle || x.poste);
    for (const v of apercu.vides) if (!postesVues.has(v.poste)) postesVues.set(v.poste, v.poste);
    const parCellule = new Map<string, typeof apercu.projet>();
    for (const x of apercu.projet) {
      const cle = `${x.poste}|${x.date}`;
      const l = parCellule.get(cle) || [];
      l.push(x);
      parCellule.set(cle, l);
    }
    const videsPar = new Set(apercu.vides.map((v) => `${v.poste}|${v.date}`));
    return {
      dates,
      postes: [...postesVues.entries()].sort((a, b) => a[1].localeCompare(b[1], 'fr')).map(([code, libelle]) => ({ code, libelle })),
      parCellule, videsPar,
    };
  }, [apercu]);

  /* Les ONGLETS DES TROIS MOIS du trimestre. */
  const ongletsMois = moisPrev ? (
    <div style={{ margin: '8px 0' }}>
      <Segment ariaLabel="Mois du trimestre" valeur={moisActif ?? ""} onChange={(v) => setMoisActif(String(v))}
        options={moisDuTrim(moisPrev).map((m) => {
          const lib = libelleMois(m);
          return { valeur: m, libelle: lib.charAt(0).toUpperCase() + lib.slice(1), disabled: bornesMois(m).au < duPrev };
        })} />
    </div>
  ) : null;
  /* LE RÉCAPITULATIF DU TRIMESTRE par personne : ouvrés, dimanches/fériés,
     gardes, jours de formation — planifié + prévu (hors cases écartées). */
  const recapTrimestre = apercu ? (() => {
    const plage = (code: string) => rhPostes.find((x) => x.code === code);
    const tout = [
      ...dejaPlanifie.map((x) => ({ ...x, formation: false })),
      ...apercu.projet.filter((x) => !apercu.ecartees.has(`${x.date}|${x.poste}|${x.orgId}`))
        .map((x) => ({ date: x.date, orgId: x.orgId, poste: x.poste, heureDebut: plage(x.poste)?.plageDebut, heureFin: plage(x.poste)?.plageFin, formation: !!x.formation })),
    ];
    const df = compterDF(tout.filter((x) => !x.formation));
    const lignes = salariesPlanifiables.map((p) => {
      const siens = tout.filter((x) => x.orgId === p.orgId);
      const jours = new Set(siens.filter((x) => !x.formation && catPoste(x.poste) !== 'garde').map((x) => x.date));
      return { p, o: [...jours].filter((d) => !(chomes.has(d) || new Date(`${d}T12:00:00`).getDay() === 0)).length,
        df: df.get(p.orgId) || 0, g: siens.filter((x) => catPoste(x.poste) === 'garde').length, f: siens.filter((x) => x.formation).length };
    }).filter((l) => l.o || l.df || l.g || l.f);
    return (
      <details style={{ margin: '4px 0 10px' }}>
        <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>Récapitulatif du trimestre par personne</summary>
        {/* L10 (11/10/2026) : sous-tableau SIMPLE sur le socle. */}
        <Tableau
          id="planning-equite"
          niveau="simple"
          densite="compacte"
          lignes={lignes}
          cleLigne={(l) => l.p.orgId}
          colonnes={[
            { cle: 'personne', libelle: 'Personne', valeur: (l) => nomDe(l.p), rendu: (l) => nomDe(l.p) },
            { cle: 'ouvres', libelle: 'Ouvrés', type: 'nombre', valeur: (l) => l.o, rendu: (l) => l.o },
            { cle: 'df', libelle: 'Dim./fériés', type: 'nombre', valeur: (l) => l.df, rendu: (l) => l.df },
            { cle: 'gardes', libelle: 'Gardes', type: 'nombre', valeur: (l) => l.g, rendu: (l) => l.g },
            { cle: 'formation', libelle: 'Formation', type: 'nombre', valeur: (l) => l.f, rendu: (l) => l.f },
          ]}
        />
      </details>
    );
  })() : null;

  /* Tous les crochets ont été appelés : le refus peut sortir. Voir la note sur
     `peutLire`, plus haut — ce retour ne doit jamais remonter. */
  if (!peutLire) return <AccesRefuse />;

  return (
    <section className="planning-ecran">
      {/* La fenêtre de confirmation des actions du menu. Montée ici, une seule
          fois : chaque action l'appelle par promesse. */}
      {confirmation}
      {/* ── LE PANNEAU DES RÈGLES, DEPUIS LE PRÉVISIONNEL (13/09/2026) ───────
          `ReglesAffectation` est un composant autonome, sans prop requise :
          l'ouvrir dans une fenêtre suffit, sans dupliquer son code. La route
          `reglesplanning` reste par ailleurs entièrement fonctionnelle — ceci
          est un second chemin vers le même écran, pas un remplacement. */}
      {reglesOuvertes && (
        <Modale titre="Règles d’affectation" largeur={860} onClose={() => setReglesOuvertes(false)}
          pied={<>
            {/* La route pleine page reste joignable — ce panneau s'y AJOUTE,
                il ne la remplace pas (barre latérale, `reglesplanning`). */}
            <Bouton taille="sm" type="button" onClick={() => { setReglesOuvertes(false); naviguerVers('reglesplanning'); }}>
              Ouvrir en pleine page
            </Bouton>
            <Bouton taille="sm" type="button" onClick={() => setReglesOuvertes(false)}>Fermer</Bouton>
          </>}>
          <ReglesAffectation />
        </Modale>
      )}
      {/* ── LES DEUX ONGLETS (RQ, 30/09/2026) ─────────────────────────── */}
      {/* ⚠️ LA BARRE N'EST PLUS CONDITIONNELLE. Elle ne s'affichait que pour qui
          avait le prévisionnel ou les échanges — sinon il n'y avait qu'un seul
          onglet, donc rien à choisir. « Conventions » étant ouvert à tous ceux
          qui lisent le planning, la masquer aurait rendu l'onglet neuf
          invisible précisément aux personnes pour qui il change quelque chose. */}
      <div role="tablist" aria-label="Planning : planning, prévisionnel, échanges, conventions" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-default)', margin: '6px 0 0' }}>
        {([['encours', 'Planning'], ...(peutPrevisionnel ? [['previsionnel', 'Prévisionnel']] : []), ...(estRh ? [['echanges', 'Échanges']] : []), ['conventions', 'Conventions']] as [OngletPlanning, string][]).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '8px 16px', fontSize: 14,
              fontWeight: onglet === k ? 700 : 500, color: onglet === k ? 'var(--color-primary-active)' : 'var(--text-muted)',
              borderBottom: `2px solid ${onglet === k ? 'var(--color-primary-active)' : 'transparent'}`, marginBottom: -1 }}>
            {l}
            {k === 'echanges' && nbEchangesAttente > 0 && (
              <span aria-label={`${nbEchangesAttente} en attente`} title={`${nbEchangesAttente} demande(s) en attente`}
                style={{ marginInlineStart: 6, display: 'inline-block', minWidth: 18, padding: '0 6px', borderRadius: 'var(--radius-pill)', background: 'var(--amber-500)', color: 'var(--text-on-primary)', fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center' }}>
                {nbEchangesAttente}
              </span>
            )}
          </button>
        ))}
      </div>

      {onglet === 'previsionnel' && (
        <div style={{ background: 'var(--gray-100)', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)', padding: 16, marginTop: 12 }}>
          <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-body)' }}>Prévisionnel</strong> — un projet calculé à partir des règles. Rien n’est écrit
            dans le planning en cours tant que vous n’avez pas cliqué « Valider et écrire ».
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Bouton taille="sm" type="button" disabled={!moisPrev} onClick={() => moisPrev && void choisirMoisPrev(decalerMoisPrev(moisPrev, -3))}><I n="chevron-left" s={13} /></Bouton>
              <strong style={{ fontSize: 15, minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>
                {moisPrev ? `T${Math.floor((Number(moisPrev.slice(5, 7)) - 1) / 3) + 1} ${moisPrev.slice(0, 4)} — ${moisDuTrim(moisPrev).map((m) => libelleMois(m, false)).join(', ')}` : 'Recherche du trimestre…'}
              </strong>
              <Bouton taille="sm" type="button" disabled={!moisPrev} onClick={() => moisPrev && void choisirMoisPrev(decalerMoisPrev(moisPrev, 3))}><I n="chevron-right" s={13} /></Bouton>
              {dejaPlanifie.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--amber-600)' }}>⚠ {dejaPlanifie.length} affectation(s) déjà au planning sur le trimestre : le calcul ne remplit que les cases restantes{duPrev > `${moisPrev}-01` ? `, à partir du ${fmtJour(duPrev)} (jamais le passé)` : ''}.</span>
              )}
            </div>
            {modifiable && (
              <Bouton variante="primaire" type="button" disabled={genEnCours} title="Calcule ce que la génération ferait sur ces dates, sans rien écrire." onClick={() => void calculerApercu()}>
                <I n="search" s={13} /> {genEnCours ? 'Calcul…' : 'Remplir le trimestre selon les règles'}
              </Bouton>
            )}
            <Bouton taille="sm" type="button" title="Voir et gérer les règles que le prévisionnel applique." onClick={() => setReglesOuvertes(true)}>
              <I n="check" s={13} /> Règles de prévision
            </Bouton>
        {/* ── ANNULER LA DERNIÈRE GÉNÉRATION (RQ, 13/09/2026) ────────────────
                « Abandonner » n'existait qu'AVANT l'écriture. Après, il fallait
                vider la plage à la main — ce qui emporte aussi les cases saisies à
                la main. Ce bouton ne retire QUE les cases du dernier lot, et
                laisse celles qui ont été reprises depuis. */}
            {modifiable && dernierLot && (
              <Bouton taille="sm" type="button" disabled={genEnCours} title="Retire les affectations créées par la dernière génération, et elles seules." onClick={() => void (async () => {
                  const ok = await demander(
                    'Les affectations créées par cette génération seront retirées du planning.\n\n'
                    + 'Celles que vous avez saisies à la main, ou reprises depuis, ne bougent pas.\n'
                    + 'L’opération est tracée au journal.',
                    { titre: 'Annuler la dernière génération ?', confirmer: 'Annuler la génération' },
                  );
                  if (!ok) return;
                  setGenEnCours(true);
                  const r = await fetchEcriture('/api/ged/ged/rh/planning/annuler-generation', {
                    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ lot: dernierLot }),
                  });
                  const d = await r.json().catch(() => ({}));
                  setGenEnCours(false);
                  if (!r.ok) { showToast(d?.error?.message || 'Annulation refusée.', 'erreur'); return; }
                  setDernierLot(null);
                  /* ⚠️ LE COMPTE QUI NE TOMBE PAS ROND SE DIT. Sans cette phrase,
                     « 40 retirées » sur 43 générées se lirait comme une panne. */
                  showToast(d.conservees
                    ? `${d.retirees} affectation(s) retirée(s) · ${d.conservees} conservée(s) : elles ont été modifiées depuis, ce n’est plus le travail du générateur.`
                    : `${d.retirees} affectation(s) retirée(s).`);
                  await rechargerPlanning();
                })()}>
                <I n="refresh" s={13} /> Annuler la génération
              </Bouton>
            )}

          </div>

          {/* Le tableau des dimanches et fériés est RETIRÉ (RQ, 01/10/2026) : les
              décomptes O / G / D-F sont désormais sur la grille, pour le mois
              prévu puis les douze derniers mois. */}

      {onglet === 'previsionnel' && !apercu && moisPrev && moisActif && (() => {
        const v = vueMois(moisActif);
        return (
          <div style={{ ...card, padding: 12, marginBottom: 14 }}>
            <strong style={{ fontSize: 13 }}>Le trimestre avant calcul — seulement les absences prévues ou demandées</strong>
            {ongletsMois}
            <div style={{ marginTop: 8 }}>
              <PrevisionnelMois du={v.du} au={v.au} personnes={salariesPlanifiables} absences={rhAbsences} postes={rhPostes} historique12={v.dfAvant} chomes={chomes}
                titre={`Prévisionnel — ${libelleMois(moisActif)} (absences seules)`}
                projet={null} vides={[]} ecartees={new Set()} dejaPlanifie={v.deja} veille={v.veille} onBasculer={() => {}} />
            </div>
          </div>
        );
      })()}
      {/* LE PANNEAU D'APERÇU — ce que la génération FERAIT, avant qu'elle le fasse. */}
      {apercu ? (
        <div style={{ ...card, padding: 16, marginBottom: 14, border: '1px solid var(--color-primary)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <strong style={{ fontSize: 14, flex: 1 }}>
              Aperçu — {apercu.projet.length - apercu.ecartees.size} affectation(s) seront créées
              {apercu.ecartees.size ? ` · ${apercu.ecartees.size} écartée(s)` : ''}
            </strong>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              du {fmtJour(apercu.du)} au {fmtJour(apercu.au)}
            </span>
          </div>
          {/* ── ⚠️ LES RÈGLES QUI N'ONT PAS PU ÊTRE TENUES (13/09/2026) ──────
              Une préférence qu'on croit appliquée alors qu'elle ne l'est pas
              est PIRE qu'une préférence absente : on cesse de vérifier. Elles
              se disent donc ici, avant la validation, et non au journal après
              coup. Ce n'est pas une erreur — la case est remplie, ce qui vaut
              mieux qu'un poste vide — c'est un fait à connaître. */}
          {!!apercu.manquements?.length && (
            <div style={{ fontSize: 12.5, lineHeight: 1.55, padding: '10px 12px', marginBottom: 10,
              borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-600)' }}>
              <strong style={{ color: 'var(--amber-600)' }}>
                {apercu.manquements.length} case(s) où l’exigence d’équipe du poste n’a pas pu être tenue.
              </strong>
              <br />Elles sont <strong>remplies quand même</strong> — un poste que personne ne tient est pire —
              mais l’exigence n’est pas honorée&nbsp;:
              <ul style={{ margin: '5px 0 0', paddingInlineStart: 18 }}>
                {apercu.manquements.slice(0, 6).map((m, i) => (
                  <li key={`${m.date}-${m.poste}-${i}`}>{fmtJour(m.date)} · <strong>{m.poste}</strong> — {m.motif}</li>
                ))}
              </ul>
              {apercu.manquements.length > 6 && (
                <div style={{ marginTop: 4 }}>… et {apercu.manquements.length - 6} autre(s).</div>
              )}
            </div>
          )}
          {!!apercu.depassementsDimFer?.length && (
            <div style={{ fontSize: 12.5, lineHeight: 1.55, padding: '10px 12px', marginBottom: 10,
              borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-600)' }}>
              <strong style={{ color: 'var(--amber-600)' }}>
                Plafond de {apercu.depassementsDimFer[0]!.plafond} dimanches/fériés par mois dépassé {apercu.depassementsDimFer.length} fois
              </strong>{' '}— personne d’autre d’habilité ne pouvait tenir la case (garde de nuit non comptée) :
              <div style={{ marginTop: 4 }}>
                {apercu.depassementsDimFer.map((x) => `${x.nom} : ${x.nombre} en ${libelleMois(x.mois, false)}`).join(' · ')}
              </div>
            </div>
          )}
          {/* Les semaines de 7 jours ne sont plus signalées (RQ, 01/10/2026 : « ne
              plus me signaler les semaines de 7 jours ») ; le moteur les rend
              toujours (`semainesSansRepos`), rien ne s'en affiche. */}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.55 }}>
            Rien n’est écrit tant que vous n’avez pas validé. {apercuEnGrille
              ? <>Cliquez une case pour l’écarter ou la reprendre — </>
              : <>Décocher une ligne l’écarte sans redistribuer les autres — </>}
            ce qui est validé est <strong>exactement</strong> ce qui est
            affiché — un aperçu recalculé à la validation ne serait pas un aperçu.
          </p>

          {/* ── LA GRILLE, PAR DÉFAUT (13/09/2026) ─────────────────────────
              Une case CLIQUÉE bascule TOUTES les affectations qu'elle porte —
              l'effectif d'un poste peut y en compter plusieurs. Le détail
              ligne à ligne reste entièrement joignable derrière le bouton :
              c'est lui qui garde la capacité d'écarter une seule personne sur
              une case qui en porte deux, ce que le clic sur la case seule ne
              sait pas distinguer. */}
          {grilleApercu && (
            <div style={{ marginBottom: 10 }}>
              {apercuEnGrille && (
                /* LA GRILLE PAR PERSONNE (RQ, 01/10/2026) : qui est choisi, et
                   pourquoi — un clic sur une case donne les raisons du moteur. */
                <>
                {ongletsMois}
                {recapTrimestre}
                {moisActif && (() => { const v = vueMois(moisActif); return (
                <PrevisionnelMois du={v.du} au={v.au} personnes={salariesPlanifiables} absences={rhAbsences} postes={rhPostes} historique12={v.dfAvant} chomes={chomes}
                  titre={`Prévisionnel — ${libelleMois(moisActif)}`}
                  projet={v.projet} vides={v.vides} ecartees={apercu.ecartees} dejaPlanifie={v.deja} veille={v.veille}
                  onBasculer={(cles) => setApercu((st) => {
                    if (!st) return st;
                    const e = new Set(st.ecartees);
                    const toutes = cles.every((k) => e.has(k));
                    for (const k of cles) { if (toutes) e.delete(k); else e.add(k); }
                    return { ...st, ecartees: e };
                  })} />); })()}
                </>
              )}
            </div>
          )}

          {/* ⚠️ LES CASES SANS CANDIDAT D'ABORD. Elles ne s'écriront pas : ce sont
              des trous, et un trou qu'on découvre après coup est un poste non tenu. */}
          {!!apercu.vides?.length && (
            <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', marginBottom: 10 }}>
              <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                ⚠ {apercu.vides.length} case(s) sans candidat — elles resteront vides
              </strong>
              <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                {[...new Set(apercu.vides.map((v) => v.poste))].join(' · ')}
              </div>
            </div>
          )}

          {!apercuEnGrille && (
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
            <table ref={enTeteCollant} className="ged-tableau" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {apercu.projet.map((x) => {
                  const cle = `${x.date}|${x.poste}|${x.orgId}`;
                  const gardee = !apercu.ecartees.has(cle);
                  return (
                    <tr key={cle} style={{ opacity: gardee ? 1 : 0.45 }}>
                      <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', width: 28 }}>
                        <input type="checkbox" checked={gardee} aria-label={`Garder ${x.nom} sur ${x.poste} le ${x.date}`}
                          onChange={() => setApercu((s) => {
                            if (!s) return s;
                            const e = new Set(s.ecartees);
                            if (e.has(cle)) e.delete(cle); else e.add(cle);
                            return { ...s, ecartees: e };
                          })} />
                      </td>
                      <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtJour(x.date)}</td>
                      <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontFamily: 'var(--font-mono)', fontSize: 'var(--tableau-ligne-compacte-police)' }}>{x.poste}</td>
                      <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontWeight: 600 }}>{x.nom}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Bouton variante="primaire" type="button" disabled={genEnCours || apercu.projet.length === apercu.ecartees.size} onClick={async () => {
                setGenEnCours(true);
                /* VALIDATION MOIS PAR MOIS (RQ, 01/10/2026) : le calcul envoyé
                   est celui du TRIMESTRE (le même que l'aperçu) ; seules les
                   cases du mois affiché sont écrites. */
                const retenues = apercu.projet
                  .filter((x) => !apercu.ecartees.has(`${x.date}|${x.poste}|${x.orgId}`) && (!moisActif || x.date.startsWith(moisActif)))
                  .map((x) => ({ date: x.date, poste: x.poste, orgId: x.orgId }));
                const r = await fetchEcriture('/api/ged/ged/rh/planning/generer', {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ du: apercu.du, au: apercu.au, retenues }),
                });
                const d = await r.json().catch(() => ({}));
                setGenEnCours(false);
                if (!r.ok) { showToast(d?.error?.message || 'Génération refusée.', 'erreur'); return; }
                showToast(`${d.creees} affectation(s) créée(s).`);
                /* On retient le LOT : c'est lui qui rend l'annulation possible,
                   et il n'existe qu'après l'écriture. */
                setDernierLot(d.lot || null);
                await rechargerPlanning();
                /* Le mois écrit devient du RÉEL : on recalcule la suite du
                   trimestre sur cette base, et on passe au mois suivant. */
                if (moisPrev) {
                  const suivant = moisActif ? moisDuTrim(moisPrev).find((m) => m > moisActif) : null;
                  setDejaPlanifie(await lirePlage(duPrev, auPrev));
                  await calculerApercu();
                  if (suivant) setMoisActif(suivant);
                } else setApercu(null);
              }}>
              Valider et écrire {moisActif ? libelleMois(moisActif, false) : ''}
            </Bouton>
            <Bouton taille="sm" type="button" onClick={() => setApercu(null)}>Abandonner</Bouton>
          </div>
        </div>
      ) : (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Choisissez le trimestre, puis « Remplir le trimestre selon les règles ». La validation se fait mois par mois.</p>
      )}
        </div>
      )}

      {/* IMPRESSION A4 (RQ, 19/08) : la semaine tient sur une page paysage —
          seules la grille et sa légende s'impriment, réduites à l'échelle.
          ⚠️ Rendue SEULEMENT en vue semaine : la vue mensuelle porte sa propre
          feuille, et deux `zoom` imbriqués se multiplieraient. */}
      {/* Seulement dans l'onglet EN COURS : le prévisionnel porte sa propre
          feuille, et deux feuilles d'impression se superposeraient. */}
      {periode !== 'mois' && onglet === 'encours' && (
      <style>{`@media print {
        @page { size: A4 landscape; margin: 8mm; }
        body * { visibility: hidden; }
        .planning-ecran, .planning-ecran * { visibility: visible; }
        .planning-ecran { position: absolute; left: 0; top: 0; width: 100%; zoom: 0.68; }
        .planning-ecran button, .planning-ecran select, .planning-ecran input { display: none !important; }
      }`}</style>
      )}
      {posteOuvert && (
        <div role="dialog" aria-modal="true" aria-label={`Définition du poste ${posteOuvert.libelle}`}
          onClick={(e) => { if (e.target === e.currentTarget) setPosteOuvert(null); }}
          style={{ position: 'fixed', inset: 0, background: VOILE, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ ...card, width: 'min(560px, 94vw)', padding: 18, maxHeight: '86vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <div>
                <Eyebrow>Définition du poste</Eyebrow>
                <h2 style={{ margin: '2px 0 0', fontSize: 17 }}>{posteOuvert.libelle}
                  <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>{posteOuvert.code}</span>
                </h2>
              </div>
              <Bouton taille="sm" type="button" onClick={() => setPosteOuvert(null)}>Fermer</Bouton>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '7px 14px', margin: '14px 0 0', fontSize: 12.5 }}>
              <dt style={{ color: 'var(--text-muted)' }}>Catégorie</dt><dd style={{ margin: 0 }}>{CATEGORIES[posteOuvert.categorie] || posteOuvert.categorie}</dd>
              <dt style={{ color: 'var(--text-muted)' }}>Horaires</dt><dd style={{ margin: 0 }}>{posteOuvert.plageDebut || '—'} – {posteOuvert.plageFin || '—'}</dd>
              <dt style={{ color: 'var(--text-muted)' }}>Effectif cible</dt><dd style={{ margin: 0 }}>{posteOuvert.effectifCible}</dd>
              <dt style={{ color: 'var(--text-muted)' }}>Habilitations exigées</dt>
              <dd style={{ margin: 0 }}>{posteOuvert.habilitationsRequises?.length
                ? posteOuvert.habilitationsRequises.map((e) => libelleExigence(e, rhHabilitations)).join(', ')
                : 'aucune'}</dd>
              <dt style={{ color: 'var(--text-muted)' }}>Ouverture</dt>
              <dd style={{ margin: 0, color: posteOuvert.calendrier?.regles?.length ? undefined : 'var(--amber-600)' }}>
                {posteOuvert.calendrier?.regles?.length ? posteOuvert.calendrierResume : 'non définie — à régler dans « Postes »'}
              </dd>
              {(posteOuvert.valableDu || posteOuvert.valableAu) && (<>
                <dt style={{ color: 'var(--text-muted)' }}>Validité</dt>
                <dd style={{ margin: 0 }}>{posteOuvert.valableDu || '…'} → {posteOuvert.valableAu || '…'}</dd>
              </>)}
            </dl>
            {/* Cette fenêtre dit ce que le poste EST ; la fiche dit ce qu'on
                attend de celui qui le tient. Deux questions différentes, et
                celle du planning est la première — d'où le lien plutôt qu'un
                dépliant de plus dans une fenêtre déjà pleine. */}
            <Bouton taille="sm" type="button" onClick={() => { const c = posteOuvert.code; setPosteOuvert(null); naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: c }); }} style={{ marginTop: 14 }}>
              Voir la fiche de poste
            </Bouton>
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              La définition se modifie dans l’écran <strong>Postes</strong> (accès RH). La fiche de
              poste, elle, n’est ressaisie nulle part : elle résume la grille d’habilitation, les
              horaires et les appareils, et suit ses sources le jour où elles changent.
            </p>
          </div>
        </div>
      )}
      {voirPassages && (() => {
        const annee = rhSemaine.slice(0, 4);
        const compte = new Map<string, Map<string, number>>();
        for (const a of rhAffectations) {
          if (!a.date.startsWith(annee)) continue;
          if (a.nature && !['travail', 'garde', 'astreinte'].includes(a.nature)) continue;
          const qui = nomDe(a.personnel); const quoi = a.poste?.libelle || '';
          if (!qui || !quoi) continue;
          if (!compte.has(qui)) compte.set(qui, new Map());
          const m = compte.get(qui)!;
          m.set(quoi, (m.get(quoi) || 0) + 1);
        }
        const postes = [...new Set([...compte.values()].flatMap((m) => [...m.keys()]))].sort((a, b) => a.localeCompare(b, 'fr'));
        const gens = [...compte.keys()].sort((a, b) => a.localeCompare(b, 'fr'));
        return (
          <div style={{ ...card, padding: 14, margin: '0 0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Passages par poste — {annee}</strong>
              <label style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Seuil de réhabilitation{' '}
                <input type="number" min={1} max={200} value={seuil}
                  onChange={(e) => {
                    const v = Math.max(1, Number(e.currentTarget.value) || 1);
                    setSeuil(v);
                    try { localStorage.setItem('bio24.seuil-rehabilitation', String(v)); } catch { /* privé */ }
                  }}
                  style={{ ...inputStyle, width: 70, height: 30, display: 'inline-block' }} />
                {' '}passages/an
              </label>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Le seuil AIDE à repérer — prononcer la réhabilitation reste une décision signée (habilitations).
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead><tr>
                  <th scope="col" style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', textAlign: 'start', borderBottom: '1px solid var(--border-default)' }}>Personne</th>
                  {postes.map((p) => (
                    <th scope="col" key={p} style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', textAlign: 'center', borderBottom: '1px solid var(--border-default)', whiteSpace: 'nowrap', fontSize: 'var(--tableau-entete-compacte-police)' }}>{p}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {gens.map((g) => (
                    <tr key={g}>
                      <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontWeight: 600, whiteSpace: 'nowrap' }}>{g}</td>
                      {postes.map((p) => {
                        const n = compte.get(g)?.get(p) || 0;
                        return (
                          <td key={p} style={{
                            padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', textAlign: 'center', fontFamily: 'var(--font-mono)',
                            color: n === 0 ? 'var(--text-faint)' : n >= seuil ? 'var(--green-700)' : 'var(--amber-600)',
                            fontWeight: n >= seuil ? 700 : 500,
                          }}>{n || '—'}</td>
                        );
                      })}
                    </tr>
                  ))}
                  {!gens.length && <tr><td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', color: 'var(--text-muted)' }}>Aucune affectation sur {annee}.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Planning"
        sousTitre={<>
          Nul ne tient un poste pour lequel il n’est pas habilité. Les personnes non habilitées
          apparaissent, mais barrées et non sélectionnables — passer outre relève du RQ, avec un motif
          écrit et une trace au journal. <RefISO code="6.2" />
        </>} />

      {/* ── LA BARRE DU PLANNING (RQ, 04/10/2026 : « réorganise tous ces boutons ») ──
          Deux lignes, deux natures, jamais mêlées :
            1. QUOI je regarde : l'échelle (jour / semaine / mois), la période et
               sa navigation — puis Filtres et Actions, en fin de ligne ;
            2. COMMENT je le regarde : disposition, sens, densité, fonction, et
               l'accès au calendrier des conventions — en plus petit, plus discret.
          Avant : quatre rangées successives, l'échelle séparée de sa navigation
          par deux panneaux, et la fonction proposée deux fois (menu ET liste). */}
      {onglet === 'encours' && (
      <div role="toolbar" aria-label="Planning — période et affichage"
        style={{ ...card, padding: '8px 12px', margin: '12px 0 10px', display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span role="group" aria-label="Échelle du planning" style={{ display: 'inline-flex', gap: 4 }}>
            <Segment ariaLabel="Période" valeur={periode} onChange={(p) => {
              /* Passer au Mois depuis une semaine ou un jour affiche le mois de CE
                 qu'on regardait — et non le dernier mois ouvert, qui n'a aucun
                 rapport. On ne perd plus son endroit en changeant d'échelle. */
              if (p === 'mois') { const ancre = periode === 'jour' ? jourCourant : jours[0]; if (ancre) setRhMois(ancre.slice(0, 7)); }
              setPeriode(p);
            }} options={([['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois']] as const).map(([v, l]) => ({ valeur: v, libelle: l }))} />
          </span>
          {periode !== 'mois' && (<>
            <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)' }} aria-hidden />
            <Bouton taille="sm" type="button" onClick={() => (periode === 'jour' ? decalerJour(-1) : decalerSemaine(-1))}
              aria-label={periode === 'jour' ? 'Jour précédent' : 'Semaine précédente'}>
              <I n="chevron-left" s={14} />
            </Bouton>
            <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 14, minWidth: 200, textAlign: 'center' }}>
              {periode === 'jour'
                ? libelleJourLong(jourCourant)
                : <>Semaine du {fmtJour(jours[0])} au {fmtJour(jours[6])}</>}
            </strong>
            <Bouton taille="sm" type="button" onClick={() => (periode === 'jour' ? decalerJour(1) : decalerSemaine(1))}
              aria-label={periode === 'jour' ? 'Jour suivant' : 'Semaine suivante'}>
              <I n="chevron-right" s={14} />
            </Bouton>
            <Bouton taille="sm" variante="discret" type="button" onClick={() => { setRhSemaine(lundiDe(new Date())); setJourVu(auj); }}>Aujourd’hui</Bouton>
          </>)}
          {periode === 'mois' && (<>
            <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)' }} aria-hidden />
            <Bouton taille="sm" type="button" onClick={() => setRhMois(decalerMois(rhMois, -1))} aria-label="Mois précédent">
              <I n="chevron-left" s={14} />
            </Bouton>
            <ChoixMois mois={rhMois} onChoisir={setRhMois} />
            <Bouton taille="sm" type="button" onClick={() => setRhMois(decalerMois(rhMois, 1))} aria-label="Mois suivant">
              <I n="chevron-right" s={14} />
            </Bouton>
            <Bouton taille="sm" variante="discret" type="button" onClick={() => { const n = new Date(); setRhMois(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`); }}>Aujourd’hui</Bouton>
          </>)}
          <span style={{ flex: 1 }} />
          {/* « MODIFIER » : le même bouton, à la même place, pour Jour, Semaine et Mois. */}
          {modifiable && (
            <Bouton variante={modeEdition ? 'primaire' : 'contour'} taille="sm" type="button" aria-pressed={modeEdition}
              onClick={() => setModeEdition(!modeEdition)}
              title={modeEdition ? 'Referme la modification : le planning ne peut plus être changé par erreur.' : 'Ouvre la modification : affecter, retirer, déplacer et échanger.'}>
              <I n="echange" s={14} /> {modeEdition ? 'Terminer la modification' : 'Modifier'}
            </Bouton>
          )}
          {/* Les actions PROPRES AU MOIS (PDF, imprimer) se posent ici, à la place des menus de la semaine. */}
          {periode === 'mois' && <span ref={setEmplActions} style={{ display: 'contents' }} />}
          {periode !== 'mois' && (<>
        {/* Les RÉGLAGES de lecture (fonction affichée, horizon du prévisionnel)
            se replient derrière un bouton qui COMPTE ce qui est actif : un
            filtre posé puis oublié fait lire un planning partiel pour le
            planning entier, et c'est le compte qui le rattrape. */}
        <MenuActions libelle={`Filtres${voirPassages ? ' (1)' : ''}`} entrees={[
          ...(voirPassages ? [] : [{
            cle: 'passages', label: `Voir les passages ${rhSemaine.slice(0, 4)}`,
            aide: 'Le compte des passages par poste sur l’année — la matière de la réhabilitation annuelle.',
            onChoisir: () => setVoirPassages(true),
          }]),
          ...(voirPassages ? [{
            cle: 'passages-off', label: `Masquer les passages ${rhSemaine.slice(0, 4)}`,
            onChoisir: () => setVoirPassages(false),
          }] : []),
        ]} />

        {/* ── ZONE 3 — TOUT CE QUI ÉCRIT, derrière UN SEUL bouton ──────────
            ⚠️ « Imprimer » y figure aussi, bien qu'elle n'écrive rien : c'est
            une action et non un réglage d'affichage, et elle EXISTE DÉSORMAIS
            EN VUE SEMAINE — la feuille d'impression A4 paysage était là depuis
            le 19/08, seul le bouton manquait. */}
        <MenuActions entrees={[
          ...(Object.keys(largeurs).length ? [{
            cle: 'raz-largeurs', label: 'Réinitialiser les largeurs de colonnes', icon: 'refresh',
            aide: 'Revient aux largeurs par défaut. Vos réglages sont propres à ce navigateur.',
            onChoisir: reinitialiserLargeurs,
          }] : []),
          {
            cle: 'imprimer', label: 'Imprimer (A4 paysage)', icon: 'printer',
            aide: 'La grille et sa légende seules, à l’échelle, sur une page.',
            onChoisir: () => window.print(),
          },
          ...(modifiable && peutPrevisionnel ? [{
            cle: 'apercu', label: 'Ouvrir le prévisionnel', icon: 'search',
            aide: 'Onglet « Prévisionnel » : dates à prévoir, règles, aperçu — rien n’est écrit avant validation.',
            onChoisir: () => setOnglet('previsionnel'),
          }] : []),
          /* Ce qui ÉCRIT le planning (recopier, propager) n'existe que dans le mode « Modifier ». */
          ...(ecriture ? [
            {
              cle: 'recopier', label: 'Recopier la semaine précédente', icon: 'copy',
              aide: 'Reprend les affectations de la semaine d’avant, en sautant ce qui est déjà pris.',
              onChoisir: () => void (async () => {
                const ok = await demander(
                  `Recopier les affectations de la semaine précédente sur la semaine du ${fmtJour(jours[0])} ?\n\n`
                  + 'Les cases déjà planifiées, les personnes sorties et les dérogations sont sautées — '
                  + 'le détail est rendu après coup.',
                  { titre: 'Recopier la semaine précédente', confirmer: 'Recopier' },
                );
                if (ok) await recopierSemainePrecedente();
              })(),
            },
            {
              cle: 'propager', label: 'Propager un jour sur la semaine…', icon: 'refresh',
              aide: 'Étale les affectations d’un jour sur les six autres — ce n’est pas la recopie de semaine.',
              onChoisir: () => setJourAPropager(jours[0]),
            },
          ] : []),
        ]} />
          </>)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
          <span>Affichage</span>
          <span role="group" aria-label="Disposition de la grille" style={{ display: 'inline-flex', gap: 4 }}>
            <Segment ariaLabel="Vue" valeur={vue} onChange={(valeur) => { setVue(valeur); if (valeur === 'personne' && focus === 'ecarts') setFocus(null); }} options={(['poste', 'personne'] as const).map((v) => ({ valeur: v, libelle: <>Par {v}</> }))} />
          </span>
          {/* Un sélecteur, comme les autres : il dit ce qu'on VOIT, là où le bouton
              précédent nommait ce qu'on obtiendrait en cliquant (« Postes en colonnes »
              affiché alors que les jours y étaient). Absent en « par personne ». */}
          {vue === 'poste' && (
            <span role="group" aria-label="Sens de lecture" style={{ display: 'inline-flex', gap: 4 }}>
              <Segment ariaLabel="Sens de lecture" valeur={sens} onChange={setSens}
                options={[
                  { valeur: 'jours-en-colonnes' as const, libelle: 'Jours en colonnes', aide: 'Un jour par colonne, un poste par ligne.' },
                  { valeur: 'postes-en-colonnes' as const, libelle: 'Postes en colonnes', aide: 'Un poste par colonne, un jour par ligne — la forme du tableau Excel du laboratoire.' },
                ]} />
            </span>
          )}
          {/* Les réglages d'affichage propres au mois (initiales / prénoms) viennent ici. */}
          {periode === 'mois' && <span ref={setEmplAffichage} style={{ display: 'contents' }} />}
          {/* A20 — la fonction ne filtre que la vue « par poste » (25/08/2026). */}
          {periode !== 'mois' && vue === 'poste' && !!fonctionsDeclarees.length && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <label htmlFor="pl-fonction">Fonction</label>
              <select id="pl-fonction" value={fonctionVue} onChange={(ev) => setFonctionVue(ev.target.value)}
                style={{ height: 30, padding: '0 8px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, background: 'var(--surface-card)' }}>
                <option value="">Toutes les fonctions</option>
                {fonctionsDeclarees.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              {!!fonctionVue && (
                <span>
                  {postesVus.length} poste(s){postesSansFonction > 0 && ` · ${postesSansFonction} sans fonction déclarée`}
                </span>
              )}
            </span>
          )}
          <span style={{ flex: 1 }} />
        </div>
      </div>
      )}

      {ecriture && periode !== 'mois' && (
        <p role="status" style={{
          margin: '0 0 10px', padding: '8px 12px', fontSize: 12.5, borderRadius: 'var(--radius-sm)',
          background: 'var(--cyan-50)', border: '1px solid var(--color-primary)',
        }}>
          <strong>Mode modification.</strong> « + » affecte quelqu’un, « − » le retire, glisser une affectation la déplace ;
          cliquer une case (Maj pour une plage) la sélectionne pour dupliquer ou vider. « Terminer la modification » referme tout cela.
        </p>
      )}

      {/* LES DEMANDES D'ÉCHANGE À VALIDER (RQ, 01/10/2026) — au RH seulement ;
          valider réécrit le planning et notifie les deux personnes. */}
      {/* Les demandes d'échange ne s'affichent plus sur « En cours » (RQ, 04/10/2026) : la pastille de l'onglet Échanges suffit. */}
      {onglet === 'echanges' && <EchangesPlanning mode="gestion" onChange={() => { setRevEchanges((n) => n + 1); void rechargerPlanning(); }} />}
      {/* Le calendrier des conventions, tel quel : il porte déjà son titre, sa
          liste et son garde-fou d'écriture. On ne le réécrit pas, on le
          DÉPLACE — une seconde implémentation aurait divergé de la première. */}
      {onglet === 'conventions' && <Calendrier />}
      {/* PUBLICATION (RQ, 02/10/2026) : sans elle, les changements ne notifient personne. */}
      {onglet === 'encours' && modifiable && <PublicationPlanning onChange={() => void rechargerPlanning()} />}
      {onglet === 'encours' && periode === 'mois' && <PlanningMois vue={vue} sens={sens} modeEdition={modeEdition} emplacements={{ actions: emplActions, affichage: emplAffichage }} />}

      {onglet === 'encours' && periode !== 'mois' && (<>
      <BandeauConventions du={joursAffiches[0]!} au={joursAffiches[joursAffiches.length - 1]!} occurrences={occConventions.filter((o) => joursAffiches.includes(o.date))} />

      {/* ── BARRE CONTEXTUELLE DE SÉLECTION (Lot 5) ──────────────────────
          Elle n'apparaît QUE si une plage est sélectionnée, et elle annonce le
          NOMBRE EXACT d'affectations concernées — pas le nombre de cases. Un
          rectangle de vingt-huit cases peut n'en contenir que trois : annoncer
          « vider 28 » ferait renoncer à un geste anodin, ou pire, ferait
          confirmer un geste qu'on croit anodin. */}
      {plage && (
        <div style={{
          ...card, padding: '10px 14px', marginBottom: 12, display: 'flex',
          alignItems: 'center', gap: 12, flexWrap: 'wrap',
          borderInlineStart: '3px solid var(--color-primary)',
        }}>
          <strong style={{ fontSize: 13 }}>
            {affectationsPlage.length} affectation{affectationsPlage.length > 1 ? 's' : ''} sélectionnée{affectationsPlage.length > 1 ? 's' : ''}
          </strong>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {plage.postes.length} poste{plage.postes.length > 1 ? 's' : ''} ·{' '}
            {plage.dates.length === 1
              ? fmtJour(plage.dates[0])
              : `du ${fmtJour(plage.dates[0])} au ${fmtJour(plage.dates[plage.dates.length - 1])}`}
          </span>
          <span style={{ flex: 1 }} />
          {modifiable && (
            <>
              <MenuActions libelle="Dupliquer vers…" disabled={enCoursPlage || !affectationsPlage.length}
                entrees={[1, 2, 3, 4].map((n) => ({
                  cle: `dup${n}`,
                  label: `+${n} semaine${n > 1 ? 's' : ''}`,
                  aide: 'Les cases occupées, les jours fermés et les habilitations manquantes sont refusés et listés.',
                  onChoisir: () => void dupliquerLaPlage(n, 'refuser'),
                })).concat([-1].map((n) => ({
                  cle: 'dup-1', label: '−1 semaine',
                  aide: 'Copier vers la semaine précédente.',
                  onChoisir: () => void dupliquerLaPlage(n, 'refuser'),
                })))} />
              {/* ⚠️ MARQUAGE DESTRUCTIF, et INACTIF quand la plage est vide :
                  un bouton rouge qui ne peut rien supprimer apprend à cliquer
                  sur un bouton rouge. */}
              <Bouton taille="sm" variante="danger"
                disabled={enCoursPlage || !affectationsPlage.length}
                title={!affectationsPlage.length ? 'Cette plage ne contient aucune affectation.' : undefined}
                onClick={() => void viderLaPlage()}>
                <I n="trash" s={13} /> Vider ({affectationsPlage.length})
              </Bouton>
            </>
          )}
          <Bouton taille="sm" type="button" onClick={viderSelection}>
            Annuler la sélection
          </Bouton>
        </div>
      )}

      {/* ⚠️ L'ANNULATION DES DIX SECONDES. Une suppression de masse sans reprise
          possible se paie en confirmation qu'on lit de moins en moins ; avec
          elle, la confirmation redevient une formalité honnête. */}
      {annulable && (
        <BandeauAnnulation jusqua={annulable.jusqua} nb={annulable.contenu.length}
          onAnnuler={() => void annulerLeVidage()} onExpire={() => setAnnulable(null)} />
      )}

      {/* Le choix du jour à propager : une question posée À PART, parce qu'elle
          demande de désigner un jour avant de confirmer. */}
      {jourAPropager && (
        <div style={{ ...card, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 12.5 }}>Propager quel jour sur les six autres ?</strong>
          <select style={{ ...inputStyle, width: 170, height: 30 }} value={jourAPropager}
            onChange={(e) => setJourAPropager(e.target.value)}>
            {jours.map((d) => <option key={d} value={d}>{fmtJour(d)}</option>)}
          </select>
          <Bouton taille="sm" type="button" onClick={() => setJourAPropager('')}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" type="button" onClick={() => void (async () => {
              const ok = await demander(
                `Répéter les affectations du ${fmtJour(jourAPropager)} sur les six autres jours de la semaine ?\n\n`
                + 'Sont écartées, et nommées : les cases déjà prises, les personnes déjà sur un autre poste '
                + 'ce jour-là, les postes qui n’ouvrent pas, et les dérogations — une dérogation se décide, '
                + 'elle ne se répète pas.',
                { titre: 'Propager un jour', confirmer: 'Propager' },
              );
              if (ok) await propagerLeJour(jourAPropager);
            })()}>Propager</Bouton>
        </div>
      )}

      {/* Les compteurs sont des FILTRES, pas des décorations : cliquer réduit la
          grille aux lignes concernées. Ils sont SÉPARÉS parce qu'ils n'appellent
          pas la même décision — un poste que personne ne tient se comble, un
          poste incomplet s'arbitre, quelqu'un planifié hors ouverture révèle
          soit un calendrier faux, soit un planning faux. Les additionner en un
          seul nombre rendrait la distinction invisible. */}
      {/* A20 — LE PLANNING PAR FONCTION (RQ, 25/08/2026). Le sélecteur ne
          s'affiche qu'en vue « par poste » : filtrer des PERSONNES par la
          fonction de leurs postes n'aurait pas le même sens, et laisser le
          contrôle visible mais sans effet est pire que de le cacher. */}

      {(derogations > 0 || nonCouverts + incomplets + horsOuverture > 0 || sansCalendrier > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {derogations > 0 && (
            <Bouton taille="sm" onClick={() => basculerFocus('derogations')} aria-pressed={focus === 'derogations'}
              style={{ fontWeight: 700, color: 'var(--amber-600)', background: focus === 'derogations' ? 'var(--amber-100)' : 'var(--amber-50)', borderColor: 'var(--amber-500)' }}>
              ⚠ {derogations} dérogation{derogations > 1 ? 's' : ''} cette semaine
            </Bouton>
          )}
          {vue === 'poste' && nonCouverts > 0 && (
            <Bouton taille="sm" onClick={() => basculerFocus('ecarts')} aria-pressed={focus === 'ecarts'}
              style={{ fontWeight: 700, color: 'var(--red-600)', background: focus === 'ecarts' ? 'var(--red-100)' : 'var(--red-50)', borderColor: 'var(--red-500)' }}>
              {nonCouverts} poste-jour{nonCouverts > 1 ? 's' : ''} ouvert{nonCouverts > 1 ? 's' : ''} que personne ne tient
            </Bouton>
          )}
          {vue === 'poste' && incomplets > 0 && (
            <Bouton taille="sm" onClick={() => basculerFocus('ecarts')} aria-pressed={focus === 'ecarts'}
              style={{ fontWeight: 700, color: 'var(--amber-600)', background: focus === 'ecarts' ? 'var(--amber-100)' : 'var(--amber-50)', borderColor: 'var(--amber-500)' }}>
              {incomplets} en effectif incomplet
            </Bouton>
          )}
          {vue === 'poste' && horsOuverture > 0 && (
            <Bouton taille="sm" onClick={() => basculerFocus('ecarts')} aria-pressed={focus === 'ecarts'}
              style={{ fontWeight: 700, color: 'var(--amber-600)', background: focus === 'ecarts' ? 'var(--amber-100)' : 'var(--amber-50)', borderColor: 'var(--amber-500)' }}>
              {horsOuverture} planifié{horsOuverture > 1 ? 's' : ''} hors ouverture
            </Bouton>
          )}
          {focus && (
            <Bouton taille="sm" type="button" onClick={() => setFocus(null)}>Voir tout le planning</Bouton>
          )}
          {/* Ni une alerte ni un écart : un paramétrage qui reste à faire. Tant
              qu'il manque, ces postes ne sont comptés dans aucun compteur. */}
          {sansCalendrier > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {sansCalendrier} poste{sansCalendrier > 1 ? 's' : ''} sans calendrier d’ouverture — leurs écarts ne sont pas mesurés.
            </span>
          )}
        </div>
      )}

      {/* Squelette au PREMIER chargement seulement : remplacer une grille déjà
          affichée par des barres grises à chaque changement de semaine ferait
          clignoter l'écran pour rien. */}
      {!postes.length && rhChargement > 0 ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={6} /></div>
      ) : !postes.length ? (
        <EmptyState
          titre="Aucun poste de travail défini"
          indice="Le planning affecte des personnes à des postes. Commencez par déclarer vos postes et les habilitations qu’ils exigent."
        />
      ) : (
        /* LA LIGNE DES DATES FIGÉE (RQ, 01/10/2026 : « figer la ligne du haut
           pour que je voie les dates quand je défile ») — le cadre défile
           lui-même, à hauteur d'écran : l'en-tête `sticky` s'y accroche. */
        <div style={{ ...card, padding: 0, overflow: 'auto', maxHeight: 'calc(100vh - 150px)' }}>
          {/* ⚠️ LA LARGEUR DÉPEND DU SENS DE LECTURE. En jours-en-colonnes, sept
              colonnes tiennent dans la page. Transposé, il y en a autant que de
              postes — vingt-cinq — et `width: 100%` les écrasait à vingt-sept
              pixels chacune : le tableau devenait illisible (constaté par la RQ,
              30/08/2026). On impose donc une largeur MINIMALE par colonne et on
              laisse le conteneur défiler : mieux vaut faire défiler un tableau
              lisible que tout montrer d'un coup sans rien pouvoir lire. */}
          {/* ⚠️ LE CONTEXTE ENGLOBE LA TABLE SEULEMENT, jamais la page. Posé plus
              haut, les capteurs de pointeur captureraient les gestes de la barre
              d'outils et du panneau d'aperçu — et le défilement de la page
              deviendrait capricieux là où rien n'est déplaçable. */}
          <PlanningDndContext ctx={contexteDnd}>
          <table className="ged-tableau" ref={enTeteCollant} style={{
            borderCollapse: 'collapse', tableLayout: 'fixed',
            ...(sens === 'postes-en-colonnes' && vue === 'poste'
              ? { minWidth: 120 + blocsPostes.reduce((n, g) => n + g.postes.length, 0) * 116 }
              : { width: '100%', minWidth: 700 }),
          }}>
            {/* ══ SENS TRANSPOSÉ : les jours en lignes, les postes en colonnes ══
                Même cellule que l'autre sens (`celluleSemaine`), donc mêmes
                pastilles, même dérogation cerclée, même trou de couverture. */}
            {/* ⚠️ La transposition ne vaut que pour la vue POSTE. En vue
                personne, les lignes sont des gens : mettre les postes en
                colonnes rendrait un troisième tableau que personne n'a demandé,
                et le bouton semblerait ne rien faire alors qu'il ferait autre
                chose. On garde l'orientation d'origine, sans le cacher. */}
            {sens === 'postes-en-colonnes' && vue === 'poste' ? (
              <>
                <colgroup>
                  <col style={{ width: 120 }} />
                  {blocsPostes.flatMap((g) => g.postes).map((p) => <col key={p.documentId} style={{ width: 116 }} />)}
                </colgroup>
                <thead>
                  {blocsPostes.some((g) => g.libelle) && (
                    <tr>
                      <th scope="col" style={{ ...thStyle, position: 'sticky', insetInlineStart: 0, background: 'var(--gray-25)', zIndex: 2 }} />
                      {blocsPostes.map((g) => (
                        <th scope="col" key={g.cle} colSpan={g.postes.length} title={g.note || undefined}
                          style={{ ...thStyle, fontSize: 'var(--tableau-entete-compacte-police)', fontWeight: 700, letterSpacing: '.04em',
                            textTransform: 'uppercase', color: 'var(--text-muted)', background: 'var(--gray-50)' }}>
                          {g.libelle}
                        </th>
                      ))}
                    </tr>
                  )}
                  <tr>
                    <th scope="col" style={{ ...thStyle, textAlign: 'start', width: 110, position: 'sticky', insetInlineStart: 0, background: 'var(--gray-25)', zIndex: 2 }}>Jour</th>
                    {blocsPostes.flatMap((g) => g.postes).map((p) => (
                      <th scope="col" key={p.documentId} title={`${p.libelle} — ${p.code}`}
                        onClick={() => setPosteOuvert(p)} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPosteOuvert(p); } }}
                        style={{ ...thStyle, cursor: 'pointer', fontSize: 10.5, lineHeight: 1.2, padding: '4px 2px' }}>
                        {/* Le CODE, pas le libellé : « P3 — Biochimie » ne tient
                            pas dans une colonne étroite, et tronqué il ne
                            distingue plus P3 de P4. Le libellé est au survol. */}
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{p.code}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {joursAffiches.map((d) => { const i = jours.indexOf(d);
                    const cejour = d === auj;
                    return (
                      <tr key={d}>
                        <th scope="row" aria-current={cejour ? 'date' : undefined}
                          style={{ ...tdStyle, textAlign: 'start', position: 'sticky', insetInlineStart: 0, zIndex: 1,
                            background: cejour ? 'var(--cyan-50)' : 'var(--surface-card)',
                            color: cejour ? 'var(--color-primary-active)' : undefined,
                            fontWeight: cejour ? 700 : 600, fontSize: 'var(--tableau-entete-compacte-police)', whiteSpace: 'nowrap' }}>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{JOURS[i]}</span>{' '}{fmtJour(d)}
                          <LignesConventions liste={conventionsDuJour.get(d)} largeur={64} />
                        </th>
                        {blocsPostes.flatMap((g) => g.postes).map((p) => celluleSemaine(p, d))}
                      </tr>
                    );
                  })}
                </tbody>
              </>
            ) : (
              <>
            <thead>
              <tr>
                <th scope="row" style={{ ...thStyle, padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', textAlign: 'start', width: largeurDe('poste', 104), position: 'sticky', insetInlineStart: 0, top: 'var(--haut-entete, 0px)', background: 'var(--gray-25)', zIndex: 4 }}>
                  {vue === 'poste' ? 'Poste' : 'Salarié'}
                  <PoigneeColonne label={vue === 'poste' ? 'Poste' : 'Salarié'} min={LARGEUR_MIN}
                    largeur={largeurDe('poste', 104)}
                    onLargeur={(px) => poserLargeur('poste', px)}
                    onAjuster={() => poserLargeur('poste', Math.max(
                      LARGEUR_MIN,
                      /* « Ajuster au contenu » sans mesurer le DOM : on estime
                         d'après le libellé le plus long. Une mesure réelle
                         demanderait un rendu hors écran de trente-deux lignes à
                         chaque double-clic ; l'estimation se trompe de quelques
                         pixels et n'a aucun coût. */
                      8 * Math.max(...(vue === 'poste'
                        ? postesVus.map((p) => p.libelle.length)
                        : gensVus.map((g) => nomDe(g).length)), 8) + 28,
                    ))} />
                </th>
                {joursAffiches.map((d) => { const i = jours.indexOf(d);
                  /* LE JOUR MÊME SE REPÈRE D'UN COUP D'ŒIL (RQ, 26/08/2026).
                     L'en-tête de la colonne passe en couleur pleine, la date en
                     gras — c'est elle qu'on cherche, pas l'abréviation du jour.
                     ⚠️ SANS LE MOT « aujourd'hui » (RQ, 05/10/2026) : « la
                     couleur suffit pour repérer aujourd'hui ». Le mot
                     redoublait le seul repère dont l'œil a besoin, dans la
                     cellule la plus étroite de la grille — et il poussait la
                     date sur une seconde ligne quand la semaine se resserre.
                     Il reste pour qui ne voit pas la couleur : `aria-current`
                     et l'infobulle, ci-dessous. */
                  const cejour = d === auj;
                  return (
                    <th scope="row" key={d}
                      /* Dit à la voix de synthèse ce que la couleur dit à l'œil :
                         un repère qui n'existe qu'en couleur n'existe pas pour
                         tout le monde. `aria-current="date"` est l'attribut
                         prévu pour ça — pas besoin d'un texte caché. */
                      aria-current={cejour ? 'date' : undefined}
                      title={cejour ? 'Aujourd’hui' : undefined}
                      style={{
                      ...thStyle,
                      /* `sticky` et non `relative` (01/10/2026) : `relative`
                         annulait l'en-tête figé ; `sticky` sert aussi de repère
                         à la poignée de largeur. */
                      position: 'sticky', top: 'var(--haut-entete, 0px)', zIndex: 2,
                      /* ⚠️ FLUIDE par défaut (RQ, 29/09/2026 : « que la page
                         couvre toute la semaine du lundi au dimanche »). À 150 px
                         fixes, sept jours + la colonne des noms dépassaient
                         l'écran et le dimanche passait hors champ. Sans largeur
                         imposée, les jours se partagent la place ; une largeur
                         réglée à la main (poignée) reste respectée. */
                      // En vue Jour, la colonne prend toute la place : la largeur réglée vaut pour la semaine.
                      width: periode === 'jour' ? '100%' : largeurs[`j${i}`],
                      ...(cejour ? {
                        background: 'var(--color-primary-active)',
                        color: 'var(--text-on-primary)',
                        borderInlineStart: '2px solid var(--color-primary-active)',
                        borderInlineEnd: '2px solid var(--color-primary-active)',
                      } : {}),
                    }}>
                      <PoigneeColonne label={`${JOURS[i]} ${fmtJour(d)}`} min={LARGEUR_MIN}
                        largeur={largeurDe(`j${i}`, 110)}
                        onLargeur={(px) => poserLargeur(`j${i}`, px)}
                        onAjuster={() => elargirJour(i)} />
                      {JOURS[i]}<br />
                      <span style={{
                        fontWeight: cejour ? 700 : 500,
                        color: cejour ? 'var(--surface-card)' : 'var(--text-muted)',
                      }}>
                        {fmtJour(d)}
                      </span>
                      <LignesConventions liste={conventionsDuJour.get(d)} largeur="70%" />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {vue === 'poste' ? blocsPostes.flatMap((g) => [
                /* L'intitulé du bloc — `th scope="colgroup"` et non `td` : un
                   lecteur d'écran doit l'annoncer comme l'en-tête des rangées
                   qu'il coiffe, sinon la séparation n'existe qu'à l'œil. */
                <tr key={`bloc-${g.cle}`}>
                  <th colSpan={joursAffiches.length + 1} scope="colgroup" title={g.note || undefined}
                    style={{
                      ...tdStyle, textAlign: 'start', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: 'var(--gray-50)',
                      fontSize: 'var(--tableau-entete-compacte-police)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                      color: 'var(--text-muted)', position: 'sticky', insetInlineStart: 0,
                    }}>
                    {g.libelle}
                  </th>
                </tr>,
                ...g.postes.map((p) => (
                <tr key={p.documentId}>
                  {/* La grille ne porte plus les SPÉCIFICITÉS du poste (RQ,
                      19/08/2026) : exigences et calendrier encombrant chaque
                      rangée, on ne voyait plus le planning. Un CLIC sur le
                      poste ouvre sa définition ; l'infobulle en donne
                      l'essentiel au survol. Une anomalie de paramétrage
                      (ouverture non définie) reste signalée d'une pastille —
                      c'est le seul détail qui mérite d'interrompre l'œil. */}
                  <th scope="row"
                    onClick={() => setPosteOuvert(p)}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPosteOuvert(p); } }}
                    title={detailPoste(p)}
                    style={{ ...tdStyle, padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)', cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {p.libelle}
                      {!p.calendrier?.regles?.length && (
                        <span aria-label="ouverture non définie" title="Ouverture non définie — cliquer pour régler."
                          style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--amber-500)', flex: 'none' }} />
                      )}
                    </div>
                    {/* ⚠️ LE CODE TECHNIQUE NE PREND PLUS UNE LIGNE EN COMPACT.
                        `ACCUEIL1`, `P2_CHIMIE` doublaient la hauteur de
                        CHAQUE ligne pour une information qu'on ne lit qu'en cas
                        de doute — et qui figure déjà dans l'infobulle du poste
                        (`detailPoste`, sur ce même `th`). En confortable il
                        reste affiché : c'est le sens de ce mode. */}
                  </th>
                  {joursAffiches.map((d) => celluleSemaine(p, d))}
                </tr>
              )),
              ]) : groupesGens.flatMap((g) => [(
                /* PAR FONCTION, puis par ordre alphabétique (RQ, 01/10/2026) —
                   comme les vues Mois et Prévisionnel. */
                <tr key={`g-${g.cle}`}>
                  <th scope="row" colSpan={joursAffiches.length + 1} style={{ ...tdStyle, textAlign: 'start', background: 'var(--gray-100)', fontSize: 'var(--tableau-entete-compacte-police)', fontWeight: 700, position: 'sticky', insetInlineStart: 0 }}>
                    {g.titre}
                  </th>
                </tr>
              ), ...g.personnes.map((pers) => {
                const total = rhAffectations.filter((a) => a.personnel?.orgId === pers.orgId).length;
                return (
                  <tr key={pers.orgId}>
                    <th scope="row" style={{ ...tdStyle, padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)' }}>
                      {/* La MÊME pastille qu'en vue Semaine et qu'en vue Mois :
                          une personne ne change pas d'apparence d'un écran à
                          l'autre — c'est la règle que ce lot doit tenir. Ici
                          c'est la LIGNE qui est la personne, la pastille se
                          pose donc devant son nom. */}
                      <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span aria-hidden style={{
                          flex: '0 0 auto', width: 8, height: 8, borderRadius: '50%',
                          background: pastilleDe(pers.orgId, couleurDe(pers.orgId)).color,
                        }} />
                        {nomDe(pers)}
                      </div>
                      <div style={{ fontSize: 12.5, color: total ? 'var(--text-faint)' : 'var(--amber-600)', marginTop: 2 }}>
                        {total ? `${total} jour(s) planifié(s)` : 'non planifié cette semaine'}
                      </div>
                    </th>
                    {joursAffiches.map((d) => {
                      const siennes = rhAffectations.filter((a) => a.date === d && a.personnel?.orgId === pers.orgId);
                      const abs = absenceLe(rhAbsences, pers.orgId, d);
                      return (
                        <td key={d} style={{ ...tdStyle, verticalAlign: 'top', background: abs ? 'var(--amber-50)' : d === auj ? 'var(--blue-50)' : undefined,
                          ...(d === auj ? { borderInlineStart: '2px solid var(--color-primary-active)', borderInlineEnd: '2px solid var(--color-primary-active)' } : {}) }}>
                          {/* Lecture seule : affecter suppose de choisir un POSTE,
                              ce que cette disposition n'affiche pas. Un « + » ici
                              devrait deviner lequel — mieux vaut ne pas l'offrir. */}
                          {siennes.map((a) => (
                            <div key={a.documentId} style={{ borderRadius: 'var(--radius-sm)', padding: '5px 7px', marginBottom: 4, background: a.derogation || (a.conflitAbsence && a.derogationAbsence) ? 'var(--amber-50)' : a.conflitAbsence ? 'var(--red-50)' : 'var(--cyan-50)', border: `1px solid ${a.derogation || (a.conflitAbsence && a.derogationAbsence) ? 'var(--amber-500)' : a.conflitAbsence ? 'var(--red-500)' : 'transparent'}` }}
                              title={a.conflitAbsence
                                ? `⚠ ${nomDe(a.personnel)} — ${a.conflitAbsence.phrase}`
                                  + (a.derogationAbsence ? `\nDérogation de ${a.derogationAbsence.auteurNom} : ${a.derogationAbsence.motif}` : '')
                                : undefined}>
                              <div style={{ fontWeight: 600, fontSize: 12.5 }}>{a.poste?.libelle || a.poste?.code || '—'}</div>
                              {/* Même règle qu'en vue « par poste » (`Case`, plus haut) : les heures
                                  disparaissent en compact, pour la même raison. */}
                              {a.derogation && <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--amber-600)' }}>⚠ DÉROGATION</div>}
                              {/* ⚠️ LE CONFLIT AVEC UN CONGÉ SE VOIT SUR LA CASE, tous les
                                  jours, jusqu'à ce que quelqu'un tranche : ou la personne
                                  revient, ou on la remplace. Un congé DEMANDÉ compte autant
                                  qu'un validé — c'est le moment où l'arbitrage est encore
                                  possible sans refaire le planning. */}
                              {a.conflitAbsence && (
                                /* ⚠️ LA DÉROGATION NE FAIT PAS DISPARAÎTRE LE CONFLIT :
                                    elle le fait passer du rouge à l'ambre, et le motif
                                    se lit au survol. Un conflit qu'une dérogation
                                    effacerait de l'écran ne serait jamais recompté.
                                    Une fois dérogé, plus rien à trancher : le badge
                                    redevient un simple texte (06/09/2026). */
                                a.derogationAbsence ? (
                                  <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--amber-600)' }}>
                                    ⚠ {a.conflitAbsence.statut === 'validee' ? 'EN CONGÉ' : 'CONGÉ DEMANDÉ'} · DÉROGÉ
                                  </div>
                                ) : (
                                  <button type="button" onClick={() => setCibleDerogation(a)}
                                    style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--red-600)', background: 'none', border: 0, padding: 0, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'var(--font-sans)' }}>
                                    ⚠ {a.conflitAbsence.statut === 'validee' ? 'EN CONGÉ' : 'CONGÉ DEMANDÉ'} · déroger
                                  </button>
                                )
                              )}
                            </div>
                          ))}
                          {abs && (
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>
                              {abs.statut === 'validee' ? 'absent' : 'absence demandée'}
                            </div>
                          )}
                          {!siennes.length && !abs && <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>—</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })])}
            </tbody>
              </>
            )}
            {/* Qui est absent, jour par jour — lisible SANS ouvrir un dialogue.
                Noms et dates seulement : le motif est une donnée de santé, et
                planifier n'exige jamais de le connaître. */}
            <tfoot>
              <tr>
                <th scope="row" style={{ ...tdStyle, textAlign: 'start', position: 'sticky', insetInlineStart: 0, background: 'var(--gray-25)', fontSize: 'var(--tableau-entete-compacte-police)', color: 'var(--text-muted)' }}>
                  Absents ce jour-là
                </th>
                {jours.map((d) => {
                  const abs = salariesPlanifiables
                    .map((p) => ({ p, a: absenceLe(rhAbsences, p.orgId, d) }))
                    .filter((x) => x.a);
                  return (
                    <td key={d} style={{ ...tdStyle, background: 'var(--gray-25)', verticalAlign: 'top' }}>
                      {abs.length === 0
                        ? <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>—</span>
                        : abs.map(({ p, a }) => (
                          <div key={p.orgId} style={{ fontSize: 12.5, color: a!.statut === 'validee' ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                            {nomDe(p)}{a!.statut === 'demandee' ? ' (demandé)' : ''}
                          </div>
                        ))}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
          </PlanningDndContext>
        </div>
      )}

      </>)}

      {cible && (
        <DialogueAffectation
          date={cible.date}
          poste={cible.poste}
          habilitations={rhHabilitations}
          personnel={rhPersonnel}
          absences={rhAbsences}
          detenues={detenuesParPersonne}
          fonctions={fonctionsOrg}
          dejaPlaces={rhAffectations.filter((a) => a.date === cible.date).map((a) => a.personnel?.orgId || '')}
          estRH={session.roles.includes('rh')}
          onFermer={() => setCible(null)}
          onValider={affecter}
        />
      )}

      {cibleDerogation && (
        <DialogueDerogationAbsence
          affectation={cibleDerogation}
          estRH={session.roles.includes('rh')}
          onFermer={() => setCibleDerogation(null)}
          onValider={derogerAbsence}
        />
      )}
    </section>
  );
}

const thStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.04em', color: 'var(--text-muted)', padding: '10px 8px',
  borderBottom: '1px solid var(--border-default)', background: 'var(--gray-25)', textAlign: 'center',
};
const tdStyle: React.CSSProperties = {
  padding: 8, borderBottom: '1px solid var(--border-subtle)', borderInlineStart: '1px solid var(--border-subtle)',
  fontFamily: 'var(--font-sans)', fontSize: 12.5,
};

/**
 * LE BANDEAU D'ANNULATION — dix secondes, et un décompte visible.
 *
 * ⚠️ LE DÉCOMPTE EST AFFICHÉ, pas seulement écoulé. « Annuler » sans dire
 * combien de temps il reste oblige à choisir vite sans savoir de combien on
 * dispose — et l'on clique par précaution, ou l'on rate la fenêtre.
 *
 * ⚠️ Composant à part, et non un `setInterval` dans l'écran : le planning se
 * re-rend à chaque frappe de filtre, et un minuteur monté là s'y relancerait.
 * Ici il vit et meurt avec le bandeau.
 */
function BandeauAnnulation({ jusqua, nb, onAnnuler, onExpire }: {
  jusqua: number; nb: number; onAnnuler: () => void; onExpire: () => void;
}) {
  const [reste, setReste] = React.useState(() => Math.max(0, Math.ceil((jusqua - Date.now()) / 1000)));
  React.useEffect(() => {
    const t = setInterval(() => {
      const r = Math.max(0, Math.ceil((jusqua - Date.now()) / 1000));
      setReste(r);
      if (r <= 0) onExpire();
    }, 250);
    return () => clearInterval(t);
  }, [jusqua, onExpire]);
  if (reste <= 0) return null;
  return (
    <div className="no-print" style={{
      ...card, padding: '10px 14px', marginBottom: 12, display: 'flex',
      alignItems: 'center', gap: 12, flexWrap: 'wrap',
      background: 'var(--amber-50)', border: '1px solid var(--amber-500)',
    }}>
      <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
        {nb} affectation{nb > 1 ? 's' : ''} supprimée{nb > 1 ? 's' : ''}
      </strong>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Annulable encore {reste} s — passé ce délai, il faudra les ressaisir.
      </span>
      <span style={{ flex: 1 }} />
      <Bouton variante="primaire" taille="sm" type="button" onClick={onAnnuler}>
        <I n="refresh" s={13} /> Annuler la suppression
      </Bouton>
    </div>
  );
}

function Case({ a, onRetirer, modifiable, couleur }: {
  a: RhAffectation; onRetirer: (id: string) => Promise<boolean>; modifiable: boolean;
  couleur?: string | null;
}) {
  const derog = !!a.derogation;
  /* L'ABSENCE SE VOIT AUSSI EN SEMAINE « PAR POSTE » (RQ, 01/10/2026 : Lamia
     absente le 30/09, barrée au mois, rien à la semaine). Comme au mois : la
     personne reste sur la case, BARRÉE — on voit le trou ET qui devait le
     tenir. Une absence seulement demandée n'est pas barrée : elle est dite.
     Jamais le motif (donnée de santé possible). */
  const absente = a.conflitAbsence?.statut === 'validee';
  const demandee = !!a.conflitAbsence && !absente;
  return (
    /* ⚠️ `ged-case-planning` (relevé du 27/08/2026, P4b) : l'écran affiche
       jusqu'à 94 corbeilles, collées au nom de la personne. La règle CSS les
       ESTOMPE au repos et les rend pleines au survol ou au focus de la case.
       Elles ne sont PAS masquées : sur une tablette il n'y a pas de survol, et
       une commande qui n'apparaît qu'au survol y devient inatteignable — la
       feuille rétablit l'opacité pleine quand le pointeur ne survole pas
       (`@media (hover: none)`).
       La confirmation, elle, existait déjà : `ConfirmButton` demande avant de
       retirer, un clic manqué ne supprime donc personne. */
    <div className="ged-case-planning"
      title={(a.heureDebut || a.heureFin) ? `${a.heureDebut || '?'}–${a.heureFin || '?'}` : undefined}
      style={{
      position: 'relative',
      borderRadius: 'var(--radius-sm)', padding: '2px 5px', marginBottom: 2,
      /* Absente (validée) : fond ROUGE, cerclé (RQ, 01/10/2026). La dérogation
         garde sa bordure ambre, seul signal qualité de la case. */
      background: absente ? 'var(--red-50)' : derog ? 'var(--amber-50)' : 'var(--cyan-50)',
      border: `1px solid ${derog ? 'var(--amber-500)' : absente ? 'var(--red-500)' : 'transparent'}`,
    }}>
      {/* RETIRER : un « − » dans l'angle haut-droit, comme le « + » en bas à
          droite (RQ, 01/10/2026) — la poubelle prenait la place du prénom.
          La CONFIRMATION est inchangée : un clic manqué ne retire personne. */}
      {modifiable && (
        <span className="no-print" style={{ position: 'absolute', top: 0, right: 0, transform: 'scale(0.62)', transformOrigin: 'top right', lineHeight: 0 }}>
          <ConfirmButton
            label="" variant="outline" icon="moins"
            titre={`Retirer ${nomDe(a.personnel) || 'cette personne'} du planning`}
            question={`Retirer ${nomDe(a.personnel) || 'cette personne'} du planning ?`}
            confirmer="Retirer"
            onConfirm={() => onRetirer(a.documentId)}
          />
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'start', gap: 2 }}>
        {/* ⚠️ LE PRÉNOM SEUL dans la case (RQ, 30/08/2026). Une case de planning
            fait quelques dizaines de pixels : « Sayed Med Tayeb Abidi » y tenait
            sur trois lignes et poussait la case, ou se tronquait au milieu.
            Le NOM COMPLET reste au survol — le raccourci sert à lire vite, pas
            à faire disparaître l'information. */}
        {/* ── LA COULEUR EN VUE SEMAINE : UNE PASTILLE, PAS UNE BORDURE ──
            Arbitrage du 02/09/2026, et il n'est pas esthétique. La bordure
            gauche de 3 px coûtait moins de largeur — mais la case porte DÉJÀ
            une bordure, et elle est porteuse : ambre pleine quand
            l'affectation est une DÉROGATION, c'est-à-dire quelqu'un qui tient
            un poste qu'il n'est pas habilité à tenir. Y superposer une couleur
            d'identité aurait affaibli le seul signal qualité de cette vue.
            La pastille, elle, ne touche à rien, et c'est la même forme qu'en
            vue Mois — une personne garde son apparence d'un écran à l'autre.
            ⚠️ ET JAMAIS LA COULEUR SEULE : le prénom reste écrit en toutes
            lettres, la dérogation reste cerclée. Une information portée par la
            couleur seule est invisible à qui ne la distingue pas. */}
        <span aria-hidden style={{
          flex: '0 0 auto', width: 8, height: 8, borderRadius: '50%', marginTop: 4,
          background: pastilleDe(a.personnel?.orgId || '', couleur).color,
        }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          ...(absente ? { textDecoration: 'line-through', opacity: 0.6 } : {}) }}
          title={`${nomDe(a.personnel) || ''}${absente ? ' — absent(e) ce jour-là' : demandee ? ' — absence demandée, non tranchée' : ''}` || undefined}>{prenomDe(a.personnel) || '—'}</span>
      </div>
      {/* ⚠️ MASQUÉES EN COMPACT (13/09/2026, RQ : « en mode compact, masquer les
          heures »). Ce mode existe pour voir plus de postes d'un coup ; les
          heures de chaque case n'y aident pas et pèsent une ligne entière sur
          chacune des dizaines de cases affichées. */}
      {a.nature === 'astreinte' && (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>ASTREINTE</div>
      )}
      {(absente || demandee) && (
        <div style={{ fontSize: 11, fontWeight: 700, color: absente ? 'var(--red-600)' : 'var(--amber-600)' }}>{absente ? 'absent(e)' : 'absence demandée'}</div>
      )}
      {derog && (
        <div title={a.derogation?.motif} style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', marginTop: 2 }}>
          ⚠ DÉROGATION — {a.derogation?.auteurNom}
        </div>
      )}
    </div>
  );
}

/** Le choix d'une personne : les non-habilités RESTENT visibles, barrés et
 *  expliqués. Les masquer laisserait croire qu'ils n'existent pas, et le RQ ne
 *  saurait même pas qu'une dérogation est envisageable. */
function DialogueAffectation({ date, poste, personnel, absences, detenues, fonctions, dejaPlaces, estRH, habilitations, onFermer, onValider }: {
  habilitations: { code: string; libelle: string }[];
  date: string; poste: RhPoste; personnel: RhPersonnel[]; absences: RhAbsence[];
  detenues: Map<string, Map<string, string>>;
  /** les cases de l'organigramme, pour nommer une fonction en toutes lettres */
  fonctions: { id: string; label: string }[];
  dejaPlaces: string[]; estRH: boolean;
  onFermer: () => void;
  onValider: (a: { date: string; posteCode: string; personnelOrgId: string; heureDebut?: string; heureFin?: string;
    derogationMotif?: string; nature?: 'travail' | 'astreinte' | 'formation'; tuteurOrgId?: string;
    responsable?: { identifiant: string; motDePasse: string } }) => Promise<string | null>;
}) {
  const [choisi, setChoisi] = React.useState<RhPersonnel | null>(null);
  // Le RQ déroge depuis sa propre session ; sinon un responsable s'authentifie
  // pour ce seul geste, depuis le poste de qui affecte (06/09/2026).
  const authResponsable = useAutorisationResponsable();
  const [recherche, setRecherche] = React.useState('');
  const [motif, setMotif] = React.useState('');
  /* ── EN FORMATION — le chemin ORDINAIRE, distinct de la dérogation ────────
   *
   * Affecter quelqu'un sur un poste qu'il ne tient pas encore n'avait qu'une
   * issue : la dérogation du RQ. Or ce sont deux choses opposées — la
   * dérogation est exceptionnelle et engage le RQ ; la formation est ce que la
   * procédure décrit (« observation, pratique accompagnée, pratique autonome
   * supervisée »). Les confondre faisait signer des dérogations à la chaîne
   * pour du tutorat normal, ce qui vide la dérogation de son sens. */
  const [formation, setFormation] = React.useState(false);
  const [tuteur, setTuteur] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const boiteAff = React.useRef<HTMLDivElement | null>(null);
  /* ⚠️ Plus de `useEscape(true, onFermer)` : Échap contournait l'alerte de
     saisie non enregistrée. Une seule porte, celle de la croix. */
  const sortieAff = useSortieFenetre({ boite: boiteAff, onClose: onFermer });

  const tousSalaries = personnel.filter((p) => p.type === 'salarie' && p.statut !== 'sorti');

  /* ── LE FILTRE PAR FONCTION (RQ, 27/08/2026) ────────────────────────────
   *
   * « Je veux uniquement voir les fonctions d'intérêt pour le poste, les
   * autres masquées. » Le poste porte les fonctions admises ; on n'affiche
   * que celles-là.
   *
   * ⚠️ UNE LISTE VIDE NE FERME RIEN. Un poste sans fonction déclarée propose
   * TOUT LE MONDE — exactement comme un poste sans habilitation exigée ne
   * bloque personne. La règle inverse fermerait un poste par simple oubli de
   * saisie, et aucun écran ne dirait pourquoi plus aucun nom n'apparaît.
   * PLV1 est dans ce cas : son seul titulaire de 2026 est sorti sans que sa
   * fiche porte de fonction.
   *
   * ⚠️ Le nombre d'écartés est AFFICHÉ. Masquer en silence, c'est laisser
   * croire que le laboratoire ne compte que ces gens-là. */
  const admises = poste.fonctions || [];
  const convient = (p: RhPersonnel) => !admises.length
    || (p.fonctionIds || []).some((f) => admises.includes(f));
  const salaries = tousSalaries.filter(convient);
  const ecartes = tousSalaries.length - salaries.length;
  const nomFonction = (id: string) => fonctions.find((f) => f.id === id)?.label || id;

  /* ── LA RECHERCHE VA CHERCHER AU-DELÀ DU FILTRE (RQ, 06/09/2026) ────────
   *
   * « Quand je veux affecter une personne, je veux avoir une barre de
   * recherche. » Elle porte sur TOUT l'effectif, et non sur la seule liste
   * déjà filtrée par fonction — c'est justement le cas qui l'a motivée :
   * Nour Eddine Abid est préparateur, la bactériologie n'admet que
   * biologistes et techniciens, et il restait donc introuvable à l'écran
   * alors qu'il venait s'y former. Un filtre qu'on ne peut pas outrepasser
   * n'est plus un filtre, c'est un mur.
   *
   * Ce qui est trouvé hors fonctions admises est montré À PART et dit comme
   * tel : la règle du 27/08 tient toujours par défaut, la recherche ne
   * l'abroge pas, elle ouvre une porte nommée. */
  const q = clefRecherche(recherche);
  const correspond = (p: RhPersonnel) =>
    !q || clefRecherche(`${nomDe(p)} ${p.orgId}`).includes(q);

  const evalues = salaries.filter(correspond).map((p) => ({ p, v: verdictLocal(p, poste, detenues) }));
  const habilites = evalues.filter((x) => x.v.ok);
  /* ── EN FORMATION : UN GROUPE À PART, PAS UN REFUS ──────────────────────
   *
   * « Si une personne est en formation pour un poste, je veux la voir
   * aussi. » Détenir l'habilitation au niveau `formation` était rangé avec
   * les manquantes, sous le même titre « Non habilités » : celui qui
   * apprend depuis six mois s'affichait comme celui qui n'a jamais mis les
   * pieds sur la paillasse. Ce sont deux situations opposées — l'une
   * appelle un tuteur, l'autre une dérogation du RQ.
   *
   * Le groupe réunit quiconque apprend AU MOINS une des exigences : la
   * visibilité est le point, et c'est bien ce qui manquait. Le DROIT de
   * l'affecter, lui, ne bouge pas — celui à qui il manque en outre une
   * habilitation entière reste soumis au RQ, et la ligne le dit. Voir
   * l'apprenti et pouvoir le poser seul sont deux questions distinctes ;
   * les confondre aurait desserré un contrôle sous couvert d'affichage. */
  const enApprentissage = evalues.filter((x) => !x.v.ok && x.v.enFormation.length);
  const bloques = evalues.filter((x) => !x.v.ok && !x.v.enFormation.length);
  /* Révélés par la recherche SEULEMENT : hors de toute recherche, la liste
     reste celle des fonctions admises. */
  const horsFonction = q
    ? tousSalaries.filter((p) => !convient(p) && correspond(p)).map((p) => ({ p, v: verdictLocal(p, poste, detenues) }))
    : [];
  const trouves = evalues.length + horsFonction.length;

  /* Choisir quelqu'un REMET la case formation à sa valeur juste. Sans cela
     elle restait cochée d'un candidat à l'autre : on validait « en formation »
     avec le tuteur du précédent, ou pire, pour un habilité — chez qui le bloc
     n'est même plus affiché et la case devenait donc invisible. */
  const choisir = (p: RhPersonnel, apprend: boolean) => {
    setChoisi(p);
    setFormation(apprend);
    if (apprend) setMotif(''); else setTuteur('');
  };
  const verdictChoisi = choisi ? verdictLocal(choisi, poste, detenues) : null;
  const besoinDerogation = !!verdictChoisi && !verdictChoisi.ok;
  const absenceChoisi = choisi ? absenceLe(absences, choisi.orgId, date) : undefined;

  /* HORAIRES DE LA JOURNÉE, pré-remplis depuis la plage du poste et
   * MODIFIABLES (RQ, 19/08/2026).
   *
   * Le serveur acceptait déjà `heureDebut` / `heureFin` par affectation et les
   * rabattait sur la plage du poste ; l'écran, lui, ne les proposait pas. Faute
   * de pouvoir écrire un horaire particulier, quelqu'un avait créé un POSTE
   * « Régime Ramadan — horaire individuel », sans plage et sans effectif, qui
   * a porté 168 affectations en mars 2026. Un poste inventé pour contourner un
   * champ manquant à l'écran : le champ est ici, le poste peut partir.
   */
  const [h1, setH1] = React.useState(poste.plageDebut || '');
  const [h2, setH2] = React.useState(poste.plageFin || '');
  const horaireModifie = h1 !== (poste.plageDebut || '') || h2 !== (poste.plageFin || '');

  const valider = async () => {
    if (!choisi) return;
    setEnCours(true);
    const err = await onValider({
      date, posteCode: poste.code, personnelOrgId: choisi.orgId,
      heureDebut: h1 || undefined, heureFin: h2 || undefined,
      ...(formation
        ? { nature: 'formation' as const, tuteurOrgId: tuteur }
        : besoinDerogation ? { derogationMotif: motif.trim(), responsable: authResponsable.payload } : {}),
    });
    setEnCours(false);
    if (!err) { authResponsable.reinitialiser(); onFermer(); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`Affecter à ${poste.libelle}`}
      style={{ position: 'fixed', inset: 0, background: VOILE, display: 'grid', placeItems: 'center', zIndex: 90, padding: 16 }}
      {...sortieAff.proprietesVoile}>
      <div ref={boiteAff} {...sortieAff.proprietesBoite} style={{ ...card, padding: 22, maxWidth: 560, width: '100%', maxHeight: '86vh', overflowY: 'auto', position: 'relative', ...sortieAff.proprietesBoite.style }}>
        {sortieAff.alerte && <AlerteSortie onRester={sortieAff.rester} onQuitter={sortieAff.quitterSansEnregistrer} />}
        <Eyebrow>{fmtJour(date)} · {poste.libelle}</Eyebrow>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 8px' }}>
          {poste.habilitationsRequises?.length
            ? <>Ce poste exige : <strong>{poste.habilitationsRequises.map((e) => libelleExigence(e, habilitations)).join(', ')}</strong>.</>
            : <>Ce poste n’exige aucune habilitation — toute personne de l’effectif peut le tenir.</>}
        </p>

        {/* CE QUI EST MASQUÉ SE DIT. Une liste raccourcie sans explication se
            lit « le laboratoire ne compte que ces gens-là ». */}
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.55 }}>
          {admises.length
            ? <>Réservé à : <strong>{admises.map(nomFonction).join(', ')}</strong>
              {ecartes > 0 && <> — {ecartes} personne{ecartes > 1 ? 's' : ''} d’une autre fonction {ecartes > 1 ? 'sont masquées' : 'est masquée'} ;
                la recherche ci-dessous va les chercher quand même.</>}</>
            : <>Aucune fonction n’est déclarée pour ce poste : <strong>tout l’effectif est proposé</strong>.
              Les fonctions admises se règlent dans « Postes de travail ».</>}
        </p>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="rech-aff" style={{ fontSize: 12.5, fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Rechercher une personne
          </label>
          <input id="rech-aff" type="search" value={recherche} autoComplete="off"
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Nom, prénom ou identifiant…"
            style={{ ...inputStyle, width: '100%' }} />
          {!!q && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '5px 2px 0' }}>
              {trouves === 0
                ? <>Aucune personne de l’effectif ne porte ce nom.</>
                : <>{trouves} résultat{trouves > 1 ? 's' : ''} sur {tousSalaries.length} personne{tousSalaries.length > 1 ? 's' : ''}.</>}
            </p>
          )}
        </div>

        <Liste titre={`Habilité${habilites.length > 1 ? 's' : ''} (${habilites.length})`}
          vide={admises.length && ecartes
            ? 'Personne, parmi les fonctions admises, ne détient toutes les habilitations exigées.'
            : 'Personne dans l’effectif ne détient toutes les habilitations exigées.'}>
          {habilites.map(({ p }) => (
            <LignePersonne key={p.orgId} p={p} choisi={choisi?.orgId === p.orgId} deja={dejaPlaces.includes(p.orgId)}
              absence={absenceLe(absences, p.orgId, date)}
              onChoisir={() => choisir(p, false)} />
          ))}
        </Liste>

        {/* EN FORMATION — avant les bloqués, parce que c'est le chemin
            ordinaire et non l'exception. Sélectionnable par quiconque tient le
            planning : ce n'est pas une dérogation, le garde-fou est le tuteur,
            exigé plus bas. */}
        {enApprentissage.length > 0 && (
          <Liste titre={`En formation sur ce poste (${enApprentissage.length})`} vide="">
            {enApprentissage.map(({ p, v }) => (
              <LignePersonne key={p.orgId} p={p} raison={raison(v)} choisi={choisi?.orgId === p.orgId}
                deja={dejaPlaces.includes(p.orgId)} absence={absenceLe(absences, p.orgId, date)}
                onChoisir={!v.manquantes.length || estRH ? () => choisir(p, true) : undefined} />
            ))}
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 2px 0', lineHeight: 1.55 }}>
              Apprentissage en cours : la journée s’inscrit en formation, sous un tuteur habilité,
              et ne compte pas comme pratique au maintien des compétences.
              {!estRH && enApprentissage.some((x) => x.v.manquantes.length) && (
                <> Ceux à qui une habilitation manque <em>entièrement</em> ne peuvent être posés que par le RQ.</>
              )}
            </p>
          </Liste>
        )}

        {bloques.length > 0 && (
          <Liste titre={`Non habilité${bloques.length > 1 ? 's' : ''} (${bloques.length})`} vide="">
            {bloques.map(({ p, v }) => (
              <LignePersonne key={p.orgId} p={p} bloque raison={raison(v)} choisi={choisi?.orgId === p.orgId}
                deja={dejaPlaces.includes(p.orgId)} absence={absenceLe(absences, p.orgId, date)}
                onChoisir={() => choisir(p, false)} />
            ))}
            {!estRH && (
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 2px 0' }}>
                Déroger à une habilitation manquante relève du gestionnaire RH — il peut l’autoriser
                depuis ce poste, sans y ouvrir de session.
              </p>
            )}
          </Liste>
        )}

        {/* HORS FONCTIONS ADMISES — n'existe que sous une recherche explicite.
            La porte est nommée : on ne se trompe pas de personne par accident,
            et l'écart au paramétrage du poste est écrit noir sur blanc. */}
        {horsFonction.length > 0 && (
          <Liste titre={`Hors fonctions admises (${horsFonction.length})`} vide="">
            {horsFonction.map(({ p, v }) => (
              <LignePersonne key={p.orgId} p={p} bloque={!v.ok} raison={raison(v)}
                choisi={choisi?.orgId === p.orgId} deja={dejaPlaces.includes(p.orgId)}
                absence={absenceLe(absences, p.orgId, date)}
                onChoisir={v.ok || estRH ? () => choisir(p, !v.ok && !v.manquantes.length) : undefined} />
            ))}
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 2px 0', lineHeight: 1.55 }}>
              {horsFonction.length > 1 ? 'Ces personnes n’exercent pas' : 'Cette personne n’exerce pas'} une
              fonction admise par le poste ({admises.map(nomFonction).join(', ')}). L’affectation reste
              possible — c’est le cas d’un renfort ou d’une formation — mais si elle devient habituelle,
              c’est la liste des fonctions du poste qu’il faut corriger dans « Postes de travail ».
            </p>
          </Liste>
        )}

        {/* Signalé, pas bloqué — et c'est délibéré. Le serveur ne refuse PAS
            l'affectation d'une personne absente (`createAffectation` ne consulte
            pas le registre des absences) : interdire ici seul créerait une règle
            que l'interface applique et que l'API ignore. On avertit donc, ce qui
            couvre le cas légitime (absence sur le point d'être annulée) sans
            faire croire à une protection qui n'existe pas. */}
        {absenceChoisi && (
          <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber-500)', borderRadius: 'var(--radius-md)', padding: 12, marginTop: 12 }}>
            <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
              ⚠ {nomDe(choisi)} est {absenceChoisi.statut === 'validee' ? 'absent(e)' : 'en attente de décision'} ce jour-là
            </strong>
            <p style={{ fontSize: 12.5, color: 'var(--text-body)', margin: '4px 0 0' }}>
              {absenceChoisi.statut === 'validee'
                ? <>Absence validée du {fmtJour(absenceChoisi.dateDebut)} au {fmtJour(absenceChoisi.dateFin)}. L’affectation reste possible, mais la journée ressortira en écart aux présences.</>
                : <>Demande d’absence du {fmtJour(absenceChoisi.dateDebut)} au {fmtJour(absenceChoisi.dateFin)}, pas encore tranchée. Si elle est validée, ce poste sera à recouvrir.</>}
            </p>
          </div>
        )}

        {/* ── LE TROISIÈME CHOIX, proposé AVANT la dérogation ───────────────
            Quand la personne n'est pas habilitée, la formation doit être le
            premier réflexe et la dérogation le dernier. L'ordre des blocs le
            dit : celui-ci vient en premier. */}
        {!!choisi && !verdictChoisi?.ok && (
          <div style={{ background: 'var(--gray-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 12, marginTop: 12 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer' }}>
              <input type="checkbox" checked={formation} style={{ marginTop: 3, flex: 'none' }}
                onChange={(e) => { setFormation(e.target.checked); if (e.target.checked) setMotif(''); }} />
              <span>
                <strong style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>
                  {nomDe(choisi)} vient EN FORMATION sur ce poste
                </strong>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-body)', marginTop: 3, lineHeight: 1.55 }}>
                  Ce n’est pas une dérogation : c’est le chemin ordinaire de l’apprentissage.
                  La journée sera inscrite comme telle et <strong>ne comptera pas comme pratique</strong>
                  {' '}au maintien des compétences.
                </span>
              </span>
            </label>
            {formation && (
              <div style={{ marginTop: 10 }}>
                <label htmlFor="tuteur" style={{ fontSize: 12.5, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Tuteur — obligatoirement habilité sur ce poste
                </label>
                <Selecteur id="tuteur" value={tuteur} onChange={setTuteur} style={{ ...inputStyle, width: '100%' }}>
                  <option value="">Choisir un tuteur…</option>
                  {/* Seuls les HABILITÉS sont proposés : « le tuteur est
                      obligatoirement habilité au périmètre sur lequel il forme »
                      (PR-RH-001). Le serveur le revérifie. */}
                  {habilites.filter(({ p }) => p.orgId !== choisi.orgId).map(({ p }) => (
                    <option key={p.orgId} value={p.orgId}>{nomDe(p)}</option>
                  ))}
                </Selecteur>
                {!habilites.filter(({ p }) => p.orgId !== choisi.orgId).length && (
                  <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '5px 0 0', lineHeight: 1.5 }}>
                    ⚠ Personne n’est habilité sur ce poste : il n’y a donc personne pour former.
                    C’est un constat à porter au RQ, pas un obstacle à contourner.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {besoinDerogation && !formation && (
          <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber-500)', borderRadius: 'var(--radius-md)', padding: 12, marginTop: 12 }}>
            <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>⚠ Dérogation à motiver</strong>
            <p style={{ fontSize: 12.5, color: 'var(--text-body)', margin: '4px 0 8px' }}>
              {nomDe(choisi)} — {raison(verdictChoisi!)}. Le motif sera inscrit au journal, à votre nom,
              et restera lisible en audit.
            </p>
            <label htmlFor="motif-derog" style={{ fontSize: 12.5, fontWeight: 600 }}>Motif de la dérogation</label>
            <textarea id="motif-derog" value={motif} onChange={(e) => setMotif(e.target.value)} rows={2}
              placeholder="Ex. : supervision directe du biologiste, fin de formation prévue le…"
              style={{ ...inputStyle, width: '100%', marginTop: 4 }} />
            {!estRH && (
              <div style={{ marginTop: 8 }}>
                <ChampsResponsable {...authResponsable} libelle="cette dérogation" />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
          <label style={{ fontSize: 12.5, fontWeight: 600 }}>Début
            <input type="time" value={h1} onChange={(e) => setH1(e.target.value)}
              style={{ ...inputStyle, width: 120, display: 'block', marginTop: 3 }} />
          </label>
          <label style={{ fontSize: 12.5, fontWeight: 600 }}>Fin
            <input type="time" value={h2} onChange={(e) => setH2(e.target.value)}
              style={{ ...inputStyle, width: 120, display: 'block', marginTop: 3 }} />
          </label>
          <p style={{ fontSize: 12.5, color: horaireModifie ? 'var(--amber-700)' : 'var(--text-muted)', margin: 0, flex: '1 1 200px', lineHeight: 1.45 }}>
            {horaireModifie
              ? 'Horaire particulier pour cette journée — il ne change pas la plage du poste.'
              : 'Horaire de la plage du poste. Modifiable pour une journée : régime de Ramadan, remplacement partiel, rendez-vous.'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'end', marginTop: 16 }}>
          <Bouton type="button" onClick={onFermer}>Annuler</Bouton>
          <Bouton variante="primaire" type="button" disabled={!choisi || enCours || (besoinDerogation && (motif.trim().length < 10 || (!estRH && !authResponsable.pret)))} onClick={valider}>
            {besoinDerogation ? 'Affecter sous dérogation' : absenceChoisi ? 'Affecter malgré l’absence' : 'Affecter'}
          </Bouton>
        </div>
      </div>
    </div>
  );
}

/** La dérogation NE FAIT PAS DISPARAÎTRE le conflit (`rh.ts:derogerAbsence`,
 *  06/09/2026) : elle le justifie, la case reste marquée en ambre. Le RQ
 *  déroge depuis sa propre session ; sinon un responsable s'authentifie pour
 *  ce seul geste, depuis le poste de qui a ouvert le dialogue. */
function DialogueDerogationAbsence({ affectation, estRH, onFermer, onValider }: {
  affectation: RhAffectation; estRH: boolean;
  onFermer: () => void;
  onValider: (affectationId: string, motif: string, responsable?: { identifiant: string; motDePasse: string }) => Promise<string | null>;
}) {
  const [motif, setMotif] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const authResponsable = useAutorisationResponsable();
  const boite = React.useRef<HTMLDivElement | null>(null);
  const sortie = useSortieFenetre({ boite, onClose: onFermer });

  const pret = motif.trim().length >= 10 && (estRH || authResponsable.pret);
  const valider = async () => {
    setEnCours(true);
    const err = await onValider(affectation.documentId, motif.trim(), estRH ? undefined : authResponsable.payload);
    setEnCours(false);
    if (!err) { authResponsable.reinitialiser(); onFermer(); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Déroger au conflit de congé"
      style={{ position: 'fixed', inset: 0, background: VOILE, display: 'grid', placeItems: 'center', zIndex: 90, padding: 16 }}
      {...sortie.proprietesVoile}>
      <div ref={boite} {...sortie.proprietesBoite} style={{ ...card, padding: 22, maxWidth: 480, width: '100%', position: 'relative', ...sortie.proprietesBoite.style }}>
        {sortie.alerte && <AlerteSortie onRester={sortie.rester} onQuitter={sortie.quitterSansEnregistrer} />}
        <Eyebrow>{fmtJour(affectation.date)} · {affectation.poste?.libelle}</Eyebrow>
        <p style={{ fontSize: 13, margin: '6px 0 4px', fontWeight: 600 }}>{nomDe(affectation.personnel)}</p>
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700, margin: '0 0 12px' }}>
          ⚠ {affectation.conflitAbsence?.phrase}
        </p>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          La dérogation ne fait pas disparaître le conflit : la case reste marquée, en ambre, avec le motif au survol.
        </p>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Motif (10 caractères au minimum)</label>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
          style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
          placeholder="Pourquoi maintenir cette personne malgré son congé ?" />
        {!estRH && (
          <div style={{ marginTop: 10 }}>
            <ChampsResponsable {...authResponsable} libelle="cette dérogation" />
          </div>
        )}
        <PiedActions taille="sm" style={{ marginTop: 16 }} annuler={{ onClick: onFermer }} principale={{ libelle: "Déroger", disabled: !pret || enCours, onClick: valider }} />
      </div>
    </div>
  );
}

const raison = (v: { manquantes: string[]; enFormation: string[] }) => [
  v.manquantes.length ? `non habilité en ${v.manquantes.join(', ')}` : '',
  v.enFormation.length ? `en formation en ${v.enFormation.join(', ')}` : '',
].filter(Boolean).join(' ; ');

function Liste({ titre, vide, children }: { titre: string; vide: string; children: React.ReactNode }) {
  const vides = React.Children.count(children) === 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 5 }}>{titre}</div>
      {vides ? <p style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>{vide}</p> : children}
    </div>
  );
}

function LignePersonne({ p, bloque, raison: r, choisi, deja, absence, onChoisir }: {
  p: RhPersonnel; bloque?: boolean; raison?: string; choisi: boolean; deja: boolean;
  absence?: RhAbsence;
  onChoisir?: () => void;
}) {
  const cliquable = !!onChoisir;
  return (
    <button type="button" onClick={onChoisir} disabled={!cliquable}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
        border: `1px solid ${choisi ? 'var(--color-primary-active)' : 'var(--border-subtle)'}`,
        background: choisi ? 'var(--cyan-50)' : 'var(--surface-card)',
        borderRadius: 'var(--radius-sm)', padding: '7px 9px', marginBottom: 4,
        cursor: cliquable ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-sans)',
      }}>
      <span style={{ flex: 1 }}>
        <span style={{ fontWeight: 600, fontSize: 12.5, textDecoration: bloque ? 'line-through' : 'none', color: bloque ? 'var(--text-faint)' : 'var(--text-body)' }}>
          {nomDe(p)}
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>{p.fonction}</span>
        {bloque && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--red-600)' }}>{r}</span>}
        {deja && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--amber-600)' }}>déjà planifié ce jour-là</span>}
        {absence && (
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>
            {absence.statut === 'validee' ? '⚠ absent ce jour-là' : '⚠ absence demandée, non tranchée'}
            {' '}(du {fmtJour(absence.dateDebut)} au {fmtJour(absence.dateFin)})
          </span>
        )}
      </span>
    </button>
  );
}

/* ============================= POSTES ============================= */

export function Postes() {
  const { rhAcces, rhPostes, settings, session, savePoste, rhChargement, rhHabilitations, naviguerVers } = useGed();
  const [edite, setEdite] = React.useState<Partial<RhPoste> | null>(null);
  const [q, setQ] = React.useState('');
  if (!rhAcces?.acces) return <AccesRefuse />;

  const qq = q.trim().toLowerCase();
  const postesVus = qq
    ? rhPostes.filter((p) => `${p.code} ${p.libelle} ${CATEGORIES[p.categorie] || p.categorie} ${(p.habilitationsRequises || []).map((e) => libelleExigence(e, rhHabilitations)).join(' ')}`.toLowerCase().includes(qq))
    : rhPostes;

  const estRQ = session.roles.includes('rq');
  const disciplines = (settings?.disciplines || []).filter((d) => d.active !== false).map((d) => d.name);

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Postes de travail"
        sousTitre={<>
          Chaque poste porte les habilitations qu’il exige. C’est cette liste — et elle seule — qui
          décide qui peut être planifié : un poste sans exigence n’écarte personne, une garde qui
          couvre plusieurs disciplines les exige toutes. <RefISO code="6.2" />
        </>} />

      {estRQ ? (
        <Bouton variante="primaire" style={{ margin: '14px 0' }}
          onClick={() => setEdite({ code: '', libelle: '', categorie: 'technique', habilitationsRequises: [], modeGarde: 'aucun', effectifCible: 1, actif: true })}>
          <I n="plus" s={14} /> Nouveau poste
        </Bouton>
      ) : (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '12px 0' }}>
          Consultable — définir ce qu’un poste exige comme compétence relève du RQ.
        </p>
      )}

      {/* ⚠️ RECUEILLIS ICI LE 30/08/2026, en fermant « Composition des tâches ».
          Ces deux panneaux — l'état chiffré des rattachements appareil ↔ poste,
          et la séance de reprise qui les traite fiche par fiche avec motif —
          vivaient dans cet écran-là. Supprimer l'écran les aurait emportés, et
          le laboratoire aurait perdu la seule vue d'ensemble de ce qui reste à
          rattacher.

          ⚠️ Ils sont GLOBAUX, pas propres à un poste : leur place est sur la
          liste, pas sur une fiche. Les deux s'ouvrent repliés et ne chargent
          rien tant qu'on ne les déplie pas — la liste des postes ne doit pas
          coûter deux requêtes de plus pour un panneau que personne n'ouvre. */}
      {estRQ && (
        <div style={{ display: 'grid', gap: 10, margin: '14px 0' }}>
          {/* 28/10/2026 : le rattachement appareil ↔ poste se DÉDUIT des
              habilitations ; ce panneau dit ce qui reste à faire pour cela. */}
          <EtatHabilitationsParc />
        </div>
      )}

      {/* ── OÙ L'ON GÈRE LES CHECK-LISTS (RQ, 05/10/2026) ───────────────────
          « Où est-ce que je peux gérer les check-lists reliées au poste ? » —
          elles n'avaient aucun écran. Le renvoi est ICI parce que c'est l'écran
          où on les cherche, et il est nommé : un lien « Check-lists » sans un
          mot sur ce qu'elles font n'aurait pas répondu à la question. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 0 12px' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Les <strong>check-lists</strong> rattachées aux postes — ce que la personne passe en revue,
          affiché à son mémo du jour — se gèrent à part.
        </span>
        <Bouton taille="sm" onClick={() => naviguerVers('checklists')}>
          <I n="check" s={12} />Check-lists de poste
        </Bouton>
      </div>

      {rhPostes.length > 6 && (
        <FilterBar
          q={q} onQ={setQ} placeholder="Rechercher un poste, un code, une habilitation exigée…"
          total={rhPostes.length} affiches={postesVus.length} onReset={() => setQ('')} />
      )}

      {!rhPostes.length && rhChargement > 0 ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={5} /></div>
      ) : !postesVus.length ? (
        <EmptyState
          titre={rhPostes.length === 0 ? 'Aucun poste défini' : 'Aucun poste ne correspond'}
          indice={rhPostes.length === 0
            ? 'Déclarez vos paillasses, la garde, le pré-analytique et les postes support.'
            : `${rhPostes.length} poste(s) sont définis — seule la recherche les masque.`} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {postesVus.map((p) => (
            /* LA LIGNE OUVRE LA FICHE (RQ, 19/08/2026 : « je veux que ce soit
               présenté quand on clique sur le poste »). Plus de bouton
               « Fiches de poste » dans la barre : une liste de postes à côté
               d'une liste de fiches de poste, c'est deux fois la même liste. */
            <div key={p.documentId} className="ged-row"
              role="button" tabIndex={0}
              title={`Ouvrir la fiche du poste ${p.libelle}`}
              onClick={() => naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: p.code })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: p.code });
                }
              }}
              style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'start', gap: 12, opacity: p.actif === false ? .55 : 1, cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 14 }}>{p.libelle}</strong>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>{p.code}</span>
                  <span style={{ fontSize: 10.5, background: 'var(--gray-25)', borderRadius: 'var(--radius-pill)', padding: '2px 8px', color: 'var(--text-muted)' }}>
                    {CATEGORIES[p.categorie] || p.categorie}
                  </span>
                  {p.modeGarde && p.modeGarde !== 'aucun' && (
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
                      {MODES_GARDE[p.modeGarde]}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
                  {p.habilitationsRequises?.length ? (
                    <>Exige : <strong>{p.habilitationsRequises.map((e) => libelleExigence(e, rhHabilitations)).join(', ')}</strong></>
                  ) : (
                    <Help aide="Aucune habilitation exigée : ce poste n’écarte personne. C’est le réglage attendu pour le secrétariat, le coursier ou l’entretien.">
                      Aucune habilitation exigée
                    </Help>
                  )}
                  {(p.plageDebut || p.plageFin) && <> · {p.plageDebut}–{p.plageFin}</>}
                  {p.effectifCible > 1 && <> · effectif cible : {p.effectifCible}</>}
                </div>
              </div>
              {/* ⚠️ « Modifier » ouvre la FICHE, comme le clic sur la ligne.
                  Deux boutons qui menaient à deux écrans différents pour le même
                  poste, c'était la plainte de la RQ le 25/08 : « le bouton
                  modifier et le clic ne donnent pas la même chose ». Tout se
                  fait désormais depuis la fiche. */}
              {estRQ && (
                <Bouton taille="sm" type="button" onClick={(e) => { e.stopPropagation(); naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: p.code }); }}>
                  Ouvrir
                </Bouton>
              )}
            </div>
          ))}
        </div>
      )}

      {edite && (
        <FormulairePoste
          tousLesPostes={rhPostes}
          poste={edite} disciplines={disciplines} habilitations={rhHabilitations}
          onFermer={() => setEdite(null)}
          onEnregistrer={async (p) => { const ok = await savePoste(p); if (ok) setEdite(null); }}
        />
      )}
    </section>
  );
}

export function FormulairePoste({ poste, disciplines, habilitations, tousLesPostes = [], onFermer, onEnregistrer }: {
  poste: Partial<RhPoste>; disciplines: string[];
  habilitations: { code: string; libelle: string; actif: boolean; equipementNom: string | null }[];
  /** TOUS les postes — pour dire quelles teintes sont déjà portées. Facultatif :
   *  sans eux, le sélecteur ne grise rien et c'est le serveur qui refuse (409). */
  tousLesPostes?: RhPoste[];
  onFermer: () => void; onEnregistrer: (p: Partial<RhPoste>) => Promise<void>;
}) {
  const [f, setF] = React.useState<Partial<RhPoste>>(poste);
  const [envoi, setEnvoi] = React.useState(false);
  /* Une exception de calendrier tapée (date, ouvert/fermé, effectif, motif) mais
     pas encore ajoutée vit dans le sous-composant : « Enregistrer » ne la voit
     pas. Plutôt que de la perdre en silence, on refuse d'enregistrer et on le dit. */
  const [exceptionEnCours, setExceptionEnCours] = React.useState(false);
  const nouveau = !poste.documentId;
  useEscape(!envoi, onFermer);
  const maj = (k: keyof RhPoste, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const basculer = (d: string) => {
    const l = f.habilitationsRequises || [];
    maj('habilitationsRequises', l.includes(d) ? l.filter((x) => x !== d) : [...l, d]);
  };
  // Une exigence d'habilitation est un OBJET : on la reconnaît par son code, pas
  // par égalité de valeur — deux objets identiques ne sont jamais égaux en JS.
  const basculerHab = (code: string) => {
    const l = f.habilitationsRequises || [];
    const deja = l.some((e) => typeof e !== 'string' && e.code === code);
    maj('habilitationsRequises', deja
      ? l.filter((e) => typeof e === 'string' || e.code !== code)
      : [...l, { code, niveauMin: null }]);
  };

  /* ── COULEUR DE REPÈRE (RQ, 05/10/2026) ─────────────────────────────────
   * La valeur de départ est la teinte EFFECTIVE — celle que le planning montre
   * déjà, y compris quand rien n'a jamais été choisi (le hachage du code). Une
   * liste sans sélection dirait « pas de couleur » alors qu'il y en a une à
   * l'écran.
   * Les teintes prises le sont par les autres postes actifs de LA MÊME
   * CATÉGORIE (arbitrage RQ du 05/10/2026 ; la catégorie est exactement le bloc
   * que le planning dessine) : le sélecteur les grise, le serveur refuse.
   * ⚠️ Lues sur `f.categorie` — la catégorie EN COURS DE SAISIE, pas celle en
   * base : changer de catégorie dans le formulaire change les voisins, et le
   * serveur revérifie sur cette même valeur. */
  const codePoste = String(f.code || poste.code || '');
  const teintePoste = teinteDuPoste(codePoste, f.couleur);
  /* Pas de `useMemo` : vingt-quatre postes, le calcul est négligeable — et une
     dépendance « modifiable plus tard » ferait abandonner au compilateur React
     l'optimisation de tout le composant (même raison que la fiche personnel). */
  const prisesPoste = couleursPrisesPostes(tousLesPostes, codePoste, f.categorie || 'technique');

  const boitePoste = React.useRef<HTMLDivElement | null>(null);
  const sortiePoste = useSortieFenetre({ boite: boitePoste, onClose: onFermer });

  return (
    <div role="dialog" aria-modal="true" aria-label={nouveau ? 'Nouveau poste' : `Modifier ${poste.libelle}`}
      style={{ position: 'fixed', inset: 0, background: VOILE, display: 'grid', placeItems: 'center', zIndex: 90, padding: 16 }}
      {...sortiePoste.proprietesVoile}>
      <div ref={boitePoste} {...sortiePoste.proprietesBoite} style={{ ...card, padding: 22, maxWidth: 560, width: '100%', maxHeight: '86vh', overflowY: 'auto', position: 'relative', ...sortiePoste.proprietesBoite.style }}>
        {sortiePoste.alerte && <AlerteSortie onRester={sortiePoste.rester} onQuitter={sortiePoste.quitterSansEnregistrer} />}
        <Eyebrow>{nouveau ? 'Nouveau poste' : 'Modifier le poste'}</Eyebrow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 12 }}>
          <label style={lbl}>Code
            <input style={{ ...inputStyle, width: '100%' }} value={f.code || ''} disabled={!nouveau}
              onChange={(e) => maj('code', e.target.value.toUpperCase())} placeholder="GARDE-NUIT" />
          </label>
          <label style={lbl}>Intitulé
            <input style={{ ...inputStyle, width: '100%' }} value={f.libelle || ''}
              onChange={(e) => maj('libelle', e.target.value)} placeholder="Garde de nuit" />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <label style={lbl}>Catégorie
            <select style={{ ...inputStyle, width: '100%' }} value={f.categorie || 'technique'} onChange={(e) => maj('categorie', e.target.value)}>
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={lbl}>Mode de garde
            <select style={{ ...inputStyle, width: '100%' }} value={f.modeGarde || 'aucun'} onChange={(e) => maj('modeGarde', e.target.value)}>
              {Object.entries(MODES_GARDE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>

        {/* LA COULEUR DU POSTE (RQ, 05/10/2026) — celle des pastilles du planning
            mensuel et de « Mon planning ». Même sélecteur et même palette que la
            fiche du personnel : une couleur choisie y est indiscernable d'une
            couleur calculée, donc personne ne voit quel poste a été réglé. */}
        <div style={{ marginTop: 10 }}>
          <label style={lbl} htmlFor="poste-couleur">Couleur au planning</label>
          <SelecteurCouleur id="poste-couleur" sujet="poste" teinte={teintePoste}
            initiales={abregePoste(codePoste) || '—'} prises={prisesPoste}
            onChoisir={(v) => maj('couleur', v)}
            messagePartage={(porteur) => (
              <>Cette couleur est déjà celle de {porteur}, de la même catégorie : les deux pastilles
                se confondraient dans le même bloc du planning. Choisissez-en une autre —
                l’enregistrement sera refusé sinon.</>
            )} />
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
            Tant qu’aucune couleur n’est choisie, la teinte est <strong>calculée</strong> à partir du
            code : le poste garde exactement l’apparence qu’il a aujourd’hui au planning. Deux postes
            actifs de la <strong>même catégorie</strong> ne peuvent pas porter la même — ils
            s’affichent dans le même bloc du planning. Une autre catégorie peut la reprendre.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
          <label style={lbl}>Début
            <input type="time" style={{ ...inputStyle, width: '100%' }} value={f.plageDebut || ''} onChange={(e) => maj('plageDebut', e.target.value)} />
          </label>
          <label style={lbl}>Fin
            <input type="time" style={{ ...inputStyle, width: '100%' }} value={f.plageFin || ''} onChange={(e) => maj('plageFin', e.target.value)} />
          </label>
          <label style={lbl}>Effectif cible
            <input type="number" min={1} style={{ ...inputStyle, width: '100%' }} value={f.effectifCible ?? 1} onChange={(e) => maj('effectifCible', Number(e.target.value))} />
          </label>
        </div>


        {/* ══ LES SÉANCES (RQ, 30/08/2026) ═══════════════════════════════════
            « L'accueil a en réalité plusieurs postes, 2 le matin et 1
            l'après-midi. Pareil prélèvement. Tout comme ce P7. »
            Le laboratoire contournait de deux façons : éclater en codes
            distincts (ACCUEIL1/2/3/PM — quatre codes pour un métier, dont rien
            ne dit qu'ils sont le même poste à des heures différentes), ou poser
            un effectif de 2 avec les heures de la seconde place écrites en dur
            sur chaque affectation. La séance déclare ce que ni l'un ni l'autre
            n'écrivait. */}
        <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 10, marginTop: 14 }}>
          <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 6px' }}>Séances</legend>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
            Un poste tenu à des heures différentes par des personnes différentes se découpe en
            séances : chacune a son créneau et son nombre de places.
            <br />
            <strong>Aucune séance déclarée</strong> = le poste garde son comportement actuel — une
            seule séance, sa plage et son effectif cible ci-dessus.
          </p>

          {(f.seances || []).map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.8fr 0.6fr auto', gap: 6, marginBottom: 6, alignItems: 'end' }}>
              <label style={lbl}>Libellé
                <input style={{ ...inputStyle, width: '100%' }} value={s.libelle || ''}
                  onChange={(e) => maj('seances', (f.seances || []).map((x, k) => (k === i ? { ...x, libelle: e.target.value } : x)))} />
              </label>
              <label style={lbl}>Début
                <input type="time" style={{ ...inputStyle, width: '100%' }} value={s.debut || ''}
                  onChange={(e) => maj('seances', (f.seances || []).map((x, k) => (k === i ? { ...x, debut: e.target.value } : x)))} />
              </label>
              <label style={lbl}>Fin
                <input type="time" style={{ ...inputStyle, width: '100%' }} value={s.fin || ''}
                  onChange={(e) => maj('seances', (f.seances || []).map((x, k) => (k === i ? { ...x, fin: e.target.value } : x)))} />
              </label>
              <label style={lbl}>Places
                <input type="number" min={1} style={{ ...inputStyle, width: '100%' }} value={s.effectif ?? 1}
                  onChange={(e) => maj('seances', (f.seances || []).map((x, k) => (k === i ? { ...x, effectif: Number(e.target.value) } : x)))} />
              </label>
              <Bouton taille="sm" type="button" aria-label={`Retirer ${s.libelle || 'la séance'}`} onClick={() => maj('seances', (f.seances || []).filter((_, k) => k !== i))}>×</Bouton>
            </div>
          ))}

          <Bouton taille="sm" type="button" onClick={() => maj('seances', [...(f.seances || []), {
              cle: `s${(f.seances || []).length + 1}`,
              libelle: (f.seances || []).length ? 'Après-midi' : 'Matin',
              debut: (f.seances || []).length ? '13:00' : (f.plageDebut || '07:00'),
              fin: (f.seances || []).length ? '19:00' : (f.plageFin || '15:00'),
              effectif: 1,
            }])}>
            <I n="plus" s={12} /> Ajouter une séance
          </Bouton>

          {/* ⚠️ Le total des places est rendu : il doit se reconnaître. Trois
              places réparties 2 + 1 ne se lisent pas dans « effectif cible ». */}
          {!!(f.seances || []).length && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
              {(f.seances || []).length} séance(s) · {(f.seances || []).reduce((n, s) => n + (Number(s.effectif) || 0), 0)} place(s) au total.
              L’effectif cible ci-dessus n’est plus utilisé quand des séances sont déclarées.
            </p>
          )}
        </fieldset>

        <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 10, marginTop: 14 }}>
          <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 6px' }}>Habilitations exigées</legend>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
            Ne rien cocher = le poste n’écarte personne. Une paillasse qui tourne sur plusieurs
            automates doit les exiger tous : c’est le seul moyen d’empêcher qu’une personne y soit
            seule sans savoir en tenir une partie.
          </p>

          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', margin: '10px 0 6px' }}>
            Référentiel — automates et activités
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {habilitations.filter((h) => h.actif !== false).map((h) => {
              const on = (f.habilitationsRequises || []).some((e) => typeof e !== 'string' && e.code === h.code);
              return (
                <button key={h.code} type="button" onClick={() => basculerHab(h.code)} aria-pressed={on}
                  title={h.equipementNom || 'Activité sans automate'}
                  style={{
                    fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    borderRadius: 'var(--radius-pill)', padding: '5px 11px',
                    border: `1px solid ${on ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                    background: on ? 'var(--cyan-50)' : 'var(--surface-card)',
                    color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
                  }}>
                  {h.libelle}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', margin: '14px 0 6px' }}>
            Disciplines — ancien modèle, encore utilisé par l’encadrement
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {disciplines.map((d) => {
              const on = (f.habilitationsRequises || []).includes(d);
              return (
                <button key={d} type="button" onClick={() => basculer(d)} aria-pressed={on}
                  style={{
                    fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    borderRadius: 'var(--radius-pill)', padding: '5px 11px',
                    border: `1px solid ${on ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                    background: on ? 'var(--cyan-50)' : 'var(--surface-card)',
                    color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
                  }}>
                  {d}
                </button>
              );
            })}
          </div>
        </fieldset>

        <EditeurCalendrier valeur={f.calendrier ?? null} onChange={(c) => maj('calendrier', c)} onException={setExceptionEnCours} />

        {/* FENÊTRE DE VALIDITÉ (13/08/2026) — trois besoins distincts, un seul
            réglage : un poste qui n'existe que pendant le Ramadan, un poste
            ouvert le temps d'un stage, et un poste qu'on retire sans effacer
            l'histoire du planning. Désactiver arrête TOUT de suite et pour
            toujours ; les dates disent « de quand à quand », ce qui se
            paramètre à l'avance et ne demande pas qu'on y repense le jour dit. */}
        <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginTop: 12 }}>
          <legend style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 4px' }}>
            Période de validité
          </legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={lbl}>À partir du
              <input type="date" style={{ ...inputStyle, width: '100%' }} value={f.valableDu || ''}
                onChange={(e) => maj('valableDu', e.target.value)} />
            </label>
            <label style={lbl}>Jusqu’au (inclus)
              <input type="date" style={{ ...inputStyle, width: '100%' }} value={f.valableAu || ''}
                onChange={(e) => maj('valableAu', e.target.value)} />
            </label>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
            Laissez vide pour un poste permanent. Hors de cette fenêtre, le poste n’ouvre aucun jour :
            il ne réclame personne et ne compte aucun écart. Les affectations déjà enregistrées, elles,
            restent lisibles — on borne l’avenir, on ne réécrit pas le passé.
          </p>
        </fieldset>

        <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <input type="checkbox" checked={f.actif !== false} onChange={(e) => maj('actif', e.target.checked)} />
          Poste actif (un poste désactivé n’apparaît plus au planning)
        </label>

        {exceptionEnCours && (
          <p role="status" style={{ margin: '12px 0 0', fontSize: 12.5, fontWeight: 600, color: 'var(--amber-600)' }}>
            Une exception du calendrier est en cours de saisie : cliquez « Ajouter » pour la retenir, ou videz
            sa date pour l’abandonner. L’enregistrement la laisserait de côté sans rien dire.
          </p>
        )}
        <PiedActions taille="sm" style={{ marginTop: 16 }} annuler={{ onClick: onFermer, disabled: envoi }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: envoi, disabled: !f.code || !f.libelle || envoi || exceptionEnCours, onClick: async () => {
              setEnvoi(true);
              try { await onEnregistrer(f); } finally { setEnvoi(false); }
            } }} />
      </div>
    </div>
  );
}

const ABREGE_JOURS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

/**
 * Calendrier d'ouverture d'un poste.
 *
 * Il est FACULTATIF, et l'écran le dit : tant qu'il n'est pas rempli, le
 * planning ne signale aucun écart sur ce poste. C'est délibéré — 20 postes ne
 * se paramètrent pas en un après-midi, et faire apparaître d'un coup des
 * centaines d'anomalies le jour de la mise en service n'aurait appris à
 * personne où regarder.
 *
 * Les modèles viennent du serveur, comme les règles : proposer « lundi-vendredi »
 * d'un geste est ce qui rend le paramétrage faisable, la retouche fine venant
 * après.
 */
function EditeurCalendrier({ valeur, onChange, onException }: { valeur: RhCalendrier | null; onChange: (c: RhCalendrier | null) => void; onException?: (enCours: boolean) => void }) {
  const modeles = useGed((s) => s.rhModelesOuverture);
  const defini = !!valeur?.regles?.length;

  const majRegle = (i: number, patch: Partial<RhRegleOuverture>) => {
    if (!valeur) return;
    onChange({ ...valeur, regles: valeur.regles.map((r, k) => (k === i ? { ...r, ...patch } : r)) });
  };
  const basculerJour = (i: number, j: number) => {
    if (!valeur) return;
    const r = valeur.regles[i];
    const dedans = r.jours.includes(j);
    // Retirer un jour d'une règle ne le donne pas à une autre : il devient
    // simplement un jour de fermeture. Le déplacer d'une règle à l'autre
    // supposerait de deviner laquelle, ce qu'aucun geste n'exprime ici.
    const jours = dedans ? r.jours.filter((x) => x !== j) : [...r.jours, j].sort((a, b) => a - b);
    const autres = valeur.regles.map((x, k) => (k === i ? { ...x, jours } : { ...x, jours: dedans ? x.jours : x.jours.filter((y) => y !== j) }));
    onChange({ ...valeur, regles: autres });
  };

  return (
    <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 10, marginTop: 14 }}>
      <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 6px' }}>Calendrier d’ouverture</legend>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
        Quels jours ce poste doit-il être tenu, et par combien de personnes. Sans calendrier,
        le planning ne peut pas distinguer « fermé le dimanche » de « personne le mardi » :
        il se tait, et aucun écart n’est signalé pour ce poste.
      </p>

      {!defini ? (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', margin: '10px 0 6px' }}>
            Partir d’un modèle
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {modeles.map((m) => (
              <button key={m.code} type="button" title={m.indice}
                onClick={() => onChange(JSON.parse(JSON.stringify(m.calendrier)) as RhCalendrier)}
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)', padding: '5px 11px',
                  border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-muted)',
                }}>
                {m.libelle}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 8 }}>
            Aucun calendrier n’est défini. Vous pouvez enregistrer le poste sans en choisir un.
          </p>
        </div>
      ) : (
        <div>
          {valeur!.regles.map((r, i) => (
            <div key={i} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                {ABREGE_JOURS.map((nom, k) => {
                  const j = k + 1;
                  const on = r.jours.includes(j);
                  return (
                    <button key={j} type="button" onClick={() => basculerJour(i, j)} aria-pressed={on}
                      style={{
                        fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                        borderRadius: 'var(--radius-sm)', padding: '4px 9px',
                        border: `1px solid ${on ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                        background: on ? 'var(--cyan-50)' : 'var(--surface-card)',
                        color: on ? 'var(--color-primary-active)' : 'var(--text-faint)',
                      }}>
                      {nom}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <label style={lbl}>Effectif attendu
                  <input type="number" min={1} style={{ ...inputStyle, width: '100%' }} value={r.effectif}
                    onChange={(e) => majRegle(i, { effectif: Number(e.target.value) })} />
                </label>
                <label style={lbl}>Début (si différent)
                  <input type="time" style={{ ...inputStyle, width: '100%' }} value={r.debut || ''}
                    onChange={(e) => majRegle(i, { debut: e.target.value || null })} />
                </label>
                <label style={lbl}>Fin (si différent)
                  <input type="time" style={{ ...inputStyle, width: '100%' }} value={r.fin || ''}
                    onChange={(e) => majRegle(i, { fin: e.target.value || null })} />
                </label>
                {valeur!.regles.length > 1 && (
                  <Bouton taille="sm" type="button" onClick={() => onChange({ ...valeur!, regles: valeur!.regles.filter((_, k) => k !== i) })}>
                    Retirer
                  </Bouton>
                )}
              </div>
            </div>
          ))}

          <Bouton taille="sm" type="button" onClick={() => onChange({ ...valeur!, regles: [...valeur!.regles, { jours: [], effectif: 1 }] })} style={{ marginBottom: 10 }}>
            + Ajouter une règle (samedi allégé, garde du dimanche…)
          </Bouton>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={lbl}>Les jours fériés
              <select style={{ ...inputStyle, width: '100%' }} value={valeur!.ferie}
                onChange={(e) => onChange({ ...valeur!, ferie: e.target.value as RhCalendrier['ferie'] })}>
                <option value="ferme">Le poste est fermé</option>
                <option value="ouvert">Le poste ouvre normalement</option>
                <option value="reduit">Le poste ouvre en effectif réduit</option>
              </select>
            </label>
            {valeur!.ferie === 'reduit' && (
              <label style={lbl}>Effectif les fériés
                <input type="number" min={1} style={{ ...inputStyle, width: '100%' }} value={valeur!.effectifFerie ?? 1}
                  onChange={(e) => onChange({ ...valeur!, effectifFerie: Number(e.target.value) })} />
              </label>
            )}
          </div>

          <ExceptionsCalendrier valeur={valeur!} onChange={onChange} onEnCours={onException} />

          <Bouton taille="sm" type="button" onClick={() => onChange(null)} style={{ marginTop: 10 }}>
            Supprimer le calendrier (le poste redevient « ouverture indéterminée »)
          </Bouton>
        </div>
      )}
    </fieldset>
  );
}

/**
 * Fermetures et ouvertures exceptionnelles.
 *
 * Séparées des règles hebdomadaires parce qu'elles ne s'y ramènent pas : une
 * exception PRIME sur le férié comme sur la règle du jour. C'est le seul moyen
 * d'écrire « ce 1er mai, la garde est tenue quand même » — sans elle, une règle
 * générale écraserait la décision d'un responsable.
 */
function ExceptionsCalendrier({ valeur, onChange, onEnCours }: { valeur: RhCalendrier; onChange: (c: RhCalendrier) => void; onEnCours?: (enCours: boolean) => void }) {
  const [date, setDate] = React.useState('');
  React.useEffect(() => { onEnCours?.(!!date); }, [date, onEnCours]);
  React.useEffect(() => () => onEnCours?.(false), [onEnCours]);
  const [ouvert, setOuvert] = React.useState(false);
  const [motif, setMotif] = React.useState('');
  /* ── ⚠️ L'EFFECTIF DE L'EXCEPTION (13/09/2026) ────────────────────────────
   * « Ce sera une règle exceptionnelle pour un jour en particulier — un
   * dimanche où on sait qu'on va avoir des sollicitations de plusieurs
   * cliniques » (RQ). Le champ existait déjà côté serveur
   * (`ExceptionOuverture.effectif`, `rh-ouverture.ts`) et `ouvertureDu` le lit
   * déjà en priorité sur l'effectif de la règle du jour — il manquait
   * seulement ICI, dans le formulaire. Vide (`''`), il retombe sur l'effectif
   * de la règle hebdomadaire du jour, comme avant ce lot : ce champ AJOUTE une
   * dérogation ponctuelle, il n'en impose aucune. */
  const [effectif, setEffectif] = React.useState('');
  const liste = valeur.exceptions || [];

  const ajouter = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    const n = Number(effectif);
    const eff = ouvert && effectif.trim() && Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    onChange({ ...valeur, exceptions: [...liste.filter((e) => e.date !== date), { date, ouvert, effectif: eff, motif: motif.trim() || null }].sort((a, b) => a.date.localeCompare(b.date)) });
    setDate(''); setMotif(''); setEffectif('');
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 }}>
        Exceptions — elles priment sur tout le reste
      </div>
      {liste.map((e) => (
        <div key={e.date} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 0' }}>
          <strong style={{ fontFamily: 'var(--font-mono)' }}>{e.date}</strong>
          <span style={{ fontWeight: 700, color: e.ouvert ? 'var(--color-primary-active)' : 'var(--text-muted)' }}>
            {e.ouvert ? 'ouvert' : 'fermé'}
          </span>
          {/* L'effectif dérogé, seulement s'il en porte un : une exception
              ouverte sans effectif propre reprend celui de la règle du jour,
              et l'écrire à côté ferait croire à une valeur qui n'existe pas. */}
          {e.ouvert && e.effectif != null && (
            <span style={{ color: 'var(--text-muted)' }}>· effectif {e.effectif}</span>
          )}
          <span style={{ color: 'var(--text-muted)' }}>{e.motif || '—'}</span>
          <Bouton taille="sm" type="button" onClick={() => onChange({ ...valeur, exceptions: liste.filter((x) => x.date !== e.date) })} style={{ marginInlineStart: 'auto' }}>
            Retirer
          </Bouton>
        </div>
      ))}
      <div style={{ display: 'grid', gridTemplateColumns: ouvert ? '150px 150px 110px 1fr auto' : '150px 150px 1fr auto', gap: 8, alignItems: 'end', marginTop: 6 }}>
        <label style={lbl}>Date
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label style={lbl}>Ce jour-là
          <select style={{ ...inputStyle, width: '100%' }} value={ouvert ? '1' : '0'}
            onChange={(e) => { const v = e.target.value === '1'; setOuvert(v); if (!v) setEffectif(''); }}>
            <option value="0">Le poste est fermé</option>
            <option value="1">Le poste est ouvert</option>
          </select>
        </label>
        {/* Un effectif dérogé n'a de sens QUE si le jour est ouvert — sur un
            jour fermé, la question ne se pose pas et le champ disparaît plutôt
            que de se griser sans qu'on sache pourquoi. */}
        {ouvert && (
          <label style={lbl}>Effectif requis
            <input type="number" min={1} style={{ ...inputStyle, width: '100%' }} value={effectif}
              onChange={(e) => setEffectif(e.target.value)} placeholder="défaut" />
          </label>
        )}
        <label style={lbl}>Motif
          <input style={{ ...inputStyle, width: '100%' }} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Inventaire annuel" />
        </label>
        <Bouton taille="sm" type="button" disabled={!date} onClick={ajouter}>Ajouter</Bouton>
      </div>
    </div>
  );
}

const lbl: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)',
};

/** Refus d'accès — distinguer « pas souscrit » de « pas mon rôle » : la première
 *  ne se débloque pas par le RQ, la seconde si. */
function AccesRefuse() {
  const rhAcces = useGed((s) => s.rhAcces);
  const rhAccesErreur = useGed((s) => s.rhAccesErreur);
  const loadRhAcces = useGed((s) => s.loadRhAcces);
  if (rhAccesErreur) {
    return (
      <EmptyState
        titre="Impossible de vérifier l’accès RH"
        indice="Panne réseau ou serveur — ce n’est pas un refus d’accès, réessayez."
        action={<Bouton taille="sm" type="button" onClick={() => loadRhAcces()}>Réessayer</Bouton>}
      />
    );
  }
  return (
    <EmptyState
      titre={rhAcces && !rhAcces.forfait ? 'Module RH non inclus dans le forfait' : 'Réservé au gestionnaire RH'}
      indice={rhAcces && !rhAcces.forfait
        ? 'Ce module ne fait pas partie de l’abonnement du laboratoire. Le RQ ne peut pas le débloquer — cela relève du contrat.'
        : 'Le rôle « Gestionnaire RH / paie » s’attribue depuis l’écran Organigramme. Être RQ n’y donne pas droit : l’accès suit la fonction, pas la personne.'}
    />
  );
}
